import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePlayer } from '../contexts/PlayerContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Upload, Search, Edit, Trash2, Check, X, LogOut, Music, Play, Square, Volume2,
  Home, FileAudio, Tag, Sparkles, Eye, EyeOff, RefreshCw, BarChart3,
  Users, FileSpreadsheet, Copy, Download, UserPlus, UserMinus, Crown, Plus, Package,
  Mail, Shield, ShieldOff, Clock, AlertCircle, Drum, Waves, Zap, Settings, ListChecks, Tags
} from 'lucide-react';
import LoopNamingDialog from '../components/LoopNamingDialog';
import { API_URL } from '../utils/api';
import { MUSICAL_KEYS } from '../utils/musicalKeys';
import { GROUP_COLOR_NAME, dotForGroup, textForGroup } from '../utils/groupColors';

const PIZZLE_IMAGE = '/images/pizzle/2lnbap9h_Pizzle_Chill_-_L.png';

const AdminDashboard = () => {
  const { user, isAuthenticated, isAdmin, logout, loading: authLoading } = useAuth();
  const { currentLoop, playLoop, stop, isPlaying } = usePlayer();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [taxonomy, setTaxonomy] = useState(null);
  const [vibePacks, setVibePacks] = useState([]);
  const [activeTab, setActiveTab] = useState('published');
  
  // Published loops state
  const [publishedLoops, setPublishedLoops] = useState([]);
  const [unpublishedLoops, setUnpublishedLoops] = useState([]);
  const [stagedFiles, setStagedFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [instrumentGroupFilter, setInstrumentGroupFilter] = useState('');  // Filter by instrument group
  const [packCodeFilter, setPackCodeFilter] = useState('');  // Filter by pack_code (Phase 2)
  const [batchIdFilter, setBatchIdFilter] = useState('');    // Filter by pack_batch_id (Phase 2)
  const [availablePackCodes, setAvailablePackCodes] = useState([]);  // List of pack codes for dropdown
  const [availableBatches, setAvailableBatches] = useState([]);      // List of batches for dropdown
  const [loading, setLoading] = useState(false);
  const [loopStats, setLoopStats] = useState({ published: 0, unpublished: 0, total: 0 });
  
  // Pagination state
  const LOOPS_PER_PAGE = 100;
  const [hasMorePublished, setHasMorePublished] = useState(true);
  const [hasMoreUnpublished, setHasMoreUnpublished] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState({ total: 0, completed: 0, failed: 0, uploading: false });
  
  // Selected items for bulk operations
  const [selectedLoops, setSelectedLoops] = useState([]);
  
  // Edit dialog state
  const [editingLoop, setEditingLoop] = useState(null);
  const [editForm, setEditForm] = useState({});
  
  // Bulk tag dialog
  const [showBulkTag, setShowBulkTag] = useState(false);
  const [bulkForm, setBulkForm] = useState({});
  
  // User management state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');
  
  // Tag templates state
  const [tagTemplates, setTagTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [templateForm, setTemplateForm] = useState({});
  
  // CSV import state
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  
  // Copy tags state
  const [showCopyTags, setShowCopyTags] = useState(false);
  const [copyTagsSource, setCopyTagsSource] = useState(null);
  
  // Loop naming state
  const [showNamingDialog, setShowNamingDialog] = useState(false);
  const [namingPairedFile, setNamingPairedFile] = useState(null);
  const [pairedFiles, setPairedFiles] = useState([]);
  const [pairingErrors, setPairingErrors] = useState([]);
  
  // Bulk Naming state
  const [namingBanks, setNamingBanks] = useState(null);
  const [bulkNamingConfig, setBulkNamingConfig] = useState({
    generatePrimary: true,
    generateVariant: true,
    autoVariant: true,
    allowDuplicates: false,
    distribution: 'randomize' // 'randomize' or 'sequential'
  });
  const [selectedStagedFiles, setSelectedStagedFiles] = useState([]);
  const [bulkNamingOpen, setBulkNamingOpen] = useState(false);
  const [playingStaged, setPlayingStaged] = useState(null); // file_id currently playing
  const [editingStagedName, setEditingStagedName] = useState(null); // { fileId, name } for inline editing
  
  // Check auth
  useEffect(() => {
    // Skip auth check if user data is passed from AuthCallback
    if (location.state?.user) {
      return;
    }
    
    if (!authLoading && (!isAuthenticated || !isAdmin)) {
      // Non-admin users trying to access /admin are redirected to home (Studio)
      navigate('/');
    }
  }, [isAuthenticated, isAdmin, authLoading, navigate, location.state]);
  
  // Fetch taxonomy
  useEffect(() => {
    const fetchTaxonomy = async () => {
      try {
        const response = await fetch(`${API_URL}/api/taxonomy`);
        if (response.ok) {
          const data = await response.json();
          setTaxonomy(data);
        }
      } catch (error) {
        console.error('Failed to fetch taxonomy:', error);
      }
    };
    
    const fetchVibePacks = async () => {
      try {
        const response = await fetch(`${API_URL}/api/vibe-packs`);
        if (response.ok) {
          const data = await response.json();
          setVibePacks(data);
        }
      } catch (error) {
        console.error('Failed to fetch vibe packs:', error);
      }
    };
    
    // Fetch available pack codes for filtering (Phase 2)
    const fetchPackCodes = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/pack-codes`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setAvailablePackCodes(data);
        }
      } catch (error) {
        console.error('Failed to fetch pack codes:', error);
      }
    };
    
    // Fetch available upload batches for filtering (Phase 2)
    const fetchBatches = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/upload-batches`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setAvailableBatches(data);
        }
      } catch (error) {
        console.error('Failed to fetch batches:', error);
      }
    };
    
    fetchTaxonomy();
    fetchVibePacks();
    fetchPackCodes();
    fetchBatches();
  }, []);
  
  // Fetch naming banks
  useEffect(() => {
    const fetchNamingBanks = async () => {
      try {
        const response = await fetch(`${API_URL}/api/naming/banks`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setNamingBanks(data);
        }
      } catch (error) {
        console.error('Failed to fetch naming banks:', error);
      }
    };
    fetchNamingBanks();
  }, []);
  
  // Fetch data based on tab (initial load only)
  const fetchData = useCallback(async () => {
    setLoading(true);
    
    try {
      // Always fetch stats
      const statsRes = await fetch(`${API_URL}/api/admin/loops/stats`, { credentials: 'include' });
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setLoopStats(stats);
      }
      
      if (activeTab === 'published' || activeTab === 'unpublished') {
        const isPublished = activeTab === 'published';
        
        const params = new URLSearchParams();
        params.append('is_published', isPublished);
        params.append('limit', LOOPS_PER_PAGE);
        params.append('skip', 0);
        if (searchQuery) params.append('search', searchQuery);
        if (instrumentGroupFilter) params.append('instrument_group', instrumentGroupFilter);
        if (packCodeFilter) params.append('pack_code', packCodeFilter);
        if (batchIdFilter) params.append('pack_batch_id', batchIdFilter);
        
        // Fetch loops and templates in parallel
        const [loopsRes, templatesRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/loops?${params.toString()}`, { credentials: 'include' }),
          fetch(`${API_URL}/api/admin/templates`, { credentials: 'include' })
        ]);
        
        if (loopsRes.ok) {
          const data = await loopsRes.json();
          console.log(`Fetched ${data.length} ${isPublished ? 'published' : 'unpublished'} loops`);
          
          if (isPublished) {
            setPublishedLoops(data);
            setHasMorePublished(data.length >= LOOPS_PER_PAGE);
          } else {
            setUnpublishedLoops(data);
            setHasMoreUnpublished(data.length >= LOOPS_PER_PAGE);
          }
        }
        
        if (templatesRes && templatesRes.ok) {
          const data = await templatesRes.json();
          setTagTemplates(data);
        }
      } else if (activeTab === 'staged') {
        const response = await fetch(`${API_URL}/api/admin/staged`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setStagedFiles(data);
        }
      } else if (activeTab === 'users') {
        setUsersLoading(true);
        try {
          // Fetch users, pending invites, and audit logs in parallel
          const [usersRes, invitesRes, logsRes] = await Promise.all([
            fetch(`${API_URL}/api/admin/users`, { credentials: 'include' }),
            fetch(`${API_URL}/api/admin/users/pending-invites`, { credentials: 'include' }),
            fetch(`${API_URL}/api/admin/audit-log?limit=20`, { credentials: 'include' })
          ]);
          
          if (usersRes.ok) {
            const data = await usersRes.json();
            setUsers(data);
          }
          if (invitesRes.ok) {
            const data = await invitesRes.json();
            setPendingInvites(data);
          }
          if (logsRes.ok) {
            const data = await logsRes.json();
            setAuditLogs(data);
          }
        } catch (error) {
          console.error('Failed to fetch user data:', error);
        }
        setUsersLoading(false);
      } else if (activeTab === 'templates') {
        setTemplatesLoading(true);
        const response = await fetch(`${API_URL}/api/admin/templates`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setTagTemplates(data);
        }
        setTemplatesLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchQuery, instrumentGroupFilter, packCodeFilter, batchIdFilter]);
  
  // Load more handler
  const loadMoreLoops = async () => {
    setLoadingMore(true);
    try {
      const isPublished = activeTab === 'published';
      const currentLoops = isPublished ? publishedLoops : unpublishedLoops;
      const skip = currentLoops.length;
      
      const params = new URLSearchParams();
      params.append('is_published', isPublished);
      params.append('limit', LOOPS_PER_PAGE);
      params.append('skip', skip);
      if (searchQuery) params.append('search', searchQuery);
      if (instrumentGroupFilter) params.append('instrument_group', instrumentGroupFilter);
      if (packCodeFilter) params.append('pack_code', packCodeFilter);
      if (batchIdFilter) params.append('pack_batch_id', batchIdFilter);
      
      const response = await fetch(`${API_URL}/api/admin/loops?${params.toString()}`, { credentials: 'include' });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`Loaded ${data.length} more ${isPublished ? 'published' : 'unpublished'} loops`);
        
        if (isPublished) {
          setPublishedLoops(prev => [...prev, ...data]);
          setHasMorePublished(data.length >= LOOPS_PER_PAGE);
        } else {
          setUnpublishedLoops(prev => [...prev, ...data]);
          setHasMoreUnpublished(data.length >= LOOPS_PER_PAGE);
        }
      }
    } catch (error) {
      console.error('Failed to load more:', error);
      toast.error('Failed to load more loops');
    } finally {
      setLoadingMore(false);
    }
  };
  
  // Reset pagination when tab, search, or filter changes
  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      // Reset the lists when switching tabs or searching
      if (activeTab === 'published') {
        setPublishedLoops([]);
        setHasMorePublished(true);
      } else if (activeTab === 'unpublished') {
        setUnpublishedLoops([]);
        setHasMoreUnpublished(true);
      }
      fetchData();
    }
  }, [activeTab, searchQuery, instrumentGroupFilter, packCodeFilter, batchIdFilter, isAuthenticated, isAdmin]);
  
  // Check file pairing before upload
  const checkFilePairing = async (files) => {
    const filenames = Array.from(files).map(f => f.name);
    try {
      const response = await fetch(`${API_URL}/api/naming/check-pairing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filenames })
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Pairing check failed:', error);
    }
    return { paired: [], errors: [], valid: false };
  };
  
  // Handle file upload with progress and pairing validation
  const handleFileUpload = async (files) => {
    const fileArray = Array.from(files);
    const total = fileArray.length;
    
    // Check pairing first
    const pairingResult = await checkFilePairing(files);
    
    if (pairingResult.errors.length > 0) {
      setPairingErrors(pairingResult.errors);
      // Show errors but still allow upload
      pairingResult.errors.forEach(err => toast.error(err));
    } else {
      setPairingErrors([]);
    }
    
    // Store paired files info for naming dialog
    if (pairingResult.paired.length > 0) {
      setPairedFiles(pairingResult.paired);
    }
    
    setUploadProgress({ total, completed: 0, failed: 0, uploading: true });
    
    let completed = 0;
    let failed = 0;
    
    // Upload files in parallel (batches of 3)
    const batchSize = 3;
    for (let i = 0; i < fileArray.length; i += batchSize) {
      const batch = fileArray.slice(i, i + batchSize);
      
      await Promise.all(batch.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        try {
          const response = await fetch(`${API_URL}/api/admin/upload`, {
            method: 'POST',
            credentials: 'include',
            body: formData
          });
          
          if (response.ok) {
            completed++;
          } else {
            failed++;
            toast.error(`Failed: ${file.name}`);
          }
        } catch (error) {
          failed++;
          toast.error(`Error: ${file.name}`);
        }
        
        setUploadProgress(prev => ({ ...prev, completed, failed }));
      }));
    }
    
    setUploadProgress(prev => ({ ...prev, uploading: false }));
    
    if (completed > 0) {
      toast.success(`Uploaded ${completed} of ${total} files`);
    }
    fetchData();
  };
  
  // Open naming dialog for a paired file
  const openNamingDialog = (pairedFile) => {
    setNamingPairedFile(pairedFile);
    setShowNamingDialog(true);
  };
  
  // Create loop with naming system
  const createLoopWithName = async (namingData) => {
    const { finalName, group, wavFile, previewFile, mood_category, pack_code, pack_name } = namingData;
    
    // Find the staged file IDs
    const wavStaged = stagedFiles.find(f => f.filename === wavFile);
    const previewStaged = stagedFiles.find(f => f.filename === previewFile);
    
    if (!wavStaged) {
      throw new Error('WAV file not found in staging');
    }
    
    const response = await fetch(`${API_URL}/api/admin/staged/${wavStaged.file_id}/create-loop-named`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: finalName,
        instrument_group: group,
        mood_category: mood_category || wavStaged.mood_category || null,
        // Pack assignment fields (Phase 1)
        pack_code: pack_code || null,
        pack_name: pack_name || null
      })
    });
    
    const data = await response.json();
    
    if (response.ok) {
      const packInfo = data.pack_code ? ` (Pack: ${data.pack_code})` : '';
      toast.success(`Created loop: ${data.title}${packInfo}`);
      fetchData();
    } else {
      throw new Error(data.detail || 'Failed to create loop');
    }
  };
  
  // Create loop from staged file (legacy - fallback)
  const createLoopFromStaged = async (fileId, mood = null) => {
    // Find the staged file
    const stagedFile = stagedFiles.find(f => f.file_id === fileId);
    const instrumentGroup = stagedFile?.instrument_group;
    
    if (!instrumentGroup) {
      toast.error('Please select an instrument group first');
      return;
    }
    
    if (stagedFile?.paired_with) {
      // Open naming dialog instead of direct creation
      const wavFile = stagedFile.file_type === 'original' ? stagedFile.filename : stagedFile.paired_with;
      const previewFile = stagedFile.file_type === 'preview' ? stagedFile.filename : stagedFile.paired_with;
      
      openNamingDialog({
        base_name: stagedFile.suggested_title || stagedFile.filename.replace(/\.(wav|ogg|mp3)$/i, ''),
        wav_file: wavFile,
        preview_file: previewFile,
        mood_category: mood,
        instrument_group: instrumentGroup
      });
      return;
    }
    
    // Legacy fallback for unpaired files
    try {
      const response = await fetch(`${API_URL}/api/admin/staged/${fileId}/create-loop`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mood_category: mood,
          instrument_group: instrumentGroup
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Created loop: ${data.title}`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create loop');
      }
    } catch (error) {
      toast.error('Error creating loop');
    }
  };
  
  // Delete staged file
  const deleteStagedFile = async (fileId) => {
    try {
      await fetch(`${API_URL}/api/admin/staged/${fileId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      toast.success('File deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };
  
  // Update staged file name (inline editing)
  const updateStagedFileName = (fileId, newName) => {
    if (!newName || newName.trim() === '') {
      setEditingStagedName(null);
      return;
    }
    setStagedFiles(prev => prev.map(f => 
      f.file_id === fileId 
        ? { ...f, suggested_title: newName.trim(), bulk_named: true }
        : f
    ));
    setEditingStagedName(null);
    toast.success('Name updated');
  };
  
  // Naming banks for edit dialog
  const [editNamingBanks, setEditNamingBanks] = useState(null);
  const [editNameCollision, setEditNameCollision] = useState(null);
  const [editSearchQuery, setEditSearchQuery] = useState('');
  
  // Fetch naming banks
  useEffect(() => {
    const fetchNamingBanks = async () => {
      try {
        const response = await fetch(`${API_URL}/api/naming/banks`);
        if (response.ok) {
          const data = await response.json();
          setEditNamingBanks(data);
        }
      } catch (error) {
        console.error('Failed to fetch naming banks:', error);
      }
    };
    fetchNamingBanks();
  }, []);
  
  // Edit loop
  const openEditDialog = (loop) => {
    setEditingLoop(loop);
    setEditSearchQuery('');
    setEditNameCollision(null);
    setEditForm({
      title: loop.title,
      bpm: loop.bpm,
      key: loop.key || '',  // Musical key
      instrument_group: loop.instrument_group || '',
      mood_category: loop.mood_category || '',
      mood_tags: loop.mood_tags || [],
      style_tags: loop.style_tags || [],
      instrument_tags: loop.instrument_tags || [],
      vibe: loop.vibe || '',
      energy: loop.energy || 3,
      color_pack: loop.color_pack || '',
      pack_name: loop.pack_name || '',
      license_type: loop.license_type || 'Classroom safe',
      vibe_packs: loop.vibe_packs || []
    });
  };
  
  // Check name collision when title changes
  useEffect(() => {
    const checkCollision = async () => {
      if (!editForm.title || !editingLoop) return;
      // Don't check if title hasn't changed
      if (editForm.title === editingLoop.title) {
        setEditNameCollision(null);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/naming/check-collision`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: editForm.title, group: editForm.instrument_group || '' })
        });
        if (response.ok) {
          const data = await response.json();
          setEditNameCollision(data.collision ? data : null);
        }
      } catch (error) {
        console.error('Collision check failed:', error);
      }
    };
    
    const debounce = setTimeout(checkCollision, 300);
    return () => clearTimeout(debounce);
  }, [editForm.title, editForm.instrument_group, editingLoop]);
  
  // Get filtered base names for edit dialog
  const getEditBaseNames = () => {
    if (!editNamingBanks || !editForm.instrument_group) return [];
    const baseNames = editNamingBanks.banks[editForm.instrument_group]?.base_names || [];
    if (!editSearchQuery) return baseNames;
    return baseNames.filter(name => 
      name.toLowerCase().includes(editSearchQuery.toLowerCase())
    );
  };
  
  // Get variants for edit dialog
  const getEditVariants = () => {
    if (!editNamingBanks || !editForm.instrument_group) return [];
    return editNamingBanks.banks[editForm.instrument_group]?.variants || [];
  };
  
  // Parse current title to extract base name and variant
  const parseCurrentTitle = () => {
    const title = editForm.title || '';
    const match = title.match(/^(.+?)\s*\((.+?)\)$/);
    if (match) {
      return { baseName: match[1].trim(), variant: match[2].trim() };
    }
    return { baseName: title, variant: '' };
  };
  
  // Build title from base name and variant
  const buildTitle = (baseName, variant) => {
    if (variant) {
      return `${baseName} (${variant})`;
    }
    return baseName;
  };
  
  const saveLoop = async () => {
    // Validate name if it changed
    if (editForm.title !== editingLoop.title) {
      try {
        const validateResponse = await fetch(`${API_URL}/api/naming/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: editForm.title })
        });
        if (validateResponse.ok) {
          const validationResult = await validateResponse.json();
          if (!validationResult.valid) {
            toast.error(validationResult.error || 'Invalid loop name');
            return;
          }
        }
      } catch (error) {
        console.error('Validation failed:', error);
      }
      
      if (editNameCollision) {
        toast.error('A loop with this name already exists');
        return;
      }
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/loops/${editingLoop.loop_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      
      if (response.ok) {
        toast.success('Loop updated');
        setEditingLoop(null);
        fetchData();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to update loop');
      }
    } catch (error) {
      console.error('Error updating loop:', error);
      toast.error('Error updating loop');
    }
  };
  
  // Publish/Unpublish
  const togglePublish = async (loop) => {
    const endpoint = loop.is_published ? 'unpublish' : 'publish';
    try {
      const response = await fetch(`${API_URL}/api/admin/loops/${loop.loop_id}/${endpoint}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success(loop.is_published ? 'Loop unpublished' : 'Loop published');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update loop');
      }
    } catch (error) {
      toast.error('Error updating loop');
    }
  };
  
  // Delete loop
  const deleteLoop = async (loopId) => {
    if (!window.confirm('Are you sure you want to delete this loop?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/loops/${loopId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      toast.success('Loop deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete loop');
    }
  };
  
  // Bulk tagging
  const applyBulkTags = async () => {
    try {
      // Filter out null/undefined values from bulkForm
      const cleanedForm = Object.fromEntries(
        Object.entries(bulkForm).filter(([_, v]) => v != null && v !== '')
      );
      
      console.log('Sending bulk update:', { loop_ids: selectedLoops, ...cleanedForm });
      
      const response = await fetch(`${API_URL}/api/admin/loops/bulk-update`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          loop_ids: selectedLoops,
          ...cleanedForm
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Updated ${data.modified_count} loops`);
        setShowBulkTag(false);
        setSelectedLoops([]);
        setBulkForm({});
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update loops');
      }
    } catch (error) {
      console.error('Bulk update error:', error);
      toast.error('Error updating loops');
    }
  };
  
  // Bulk delete
  const bulkDeleteLoops = async () => {
    if (selectedLoops.length === 0) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedLoops.length} loop(s)? This cannot be undone.`
    );
    
    if (!confirmed) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/loops/bulk-delete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ loop_ids: selectedLoops })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Deleted ${data.deleted_count} loops`);
        setSelectedLoops([]);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete loops');
      }
    } catch (error) {
      toast.error('Error deleting loops');
    }
  };
  
  // Bulk publish
  const bulkPublishLoops = async () => {
    if (selectedLoops.length === 0) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/loops/bulk-publish`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ loop_ids: selectedLoops })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.failed_count > 0) {
          // Show which loops failed and why
          const failedList = data.failed.map(f => `${f.title}: missing ${f.missing.join(', ')}`).join('\n');
          toast.error(`${data.failed_count} loop(s) couldn't be published:\n${failedList}`, { duration: 8000 });
        }
        if (data.published_count > 0) {
          toast.success(`Published ${data.published_count} loop(s)`);
        }
        setSelectedLoops([]);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to publish loops');
      }
    } catch (error) {
      toast.error('Error publishing loops');
    }
  };
  
  // Bulk unpublish
  const bulkUnpublishLoops = async () => {
    if (selectedLoops.length === 0) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to unpublish ${selectedLoops.length} loop(s)? They will be moved to the unpublished queue.`
    );
    
    if (!confirmed) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/loops/bulk-unpublish`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ loop_ids: selectedLoops })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Unpublished ${data.unpublished_count} loop(s)`);
        setSelectedLoops([]);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to unpublish loops');
      }
    } catch (error) {
      toast.error('Error unpublishing loops');
    }
  };
  
  // Tag assist
  const suggestTags = async () => {
    if (!editingLoop) return;
    
    try {
      const params = new URLSearchParams();
      params.append('filename', editForm.title);
      if (editForm.pack_name) params.append('pack_name', editForm.pack_name);
      
      const response = await fetch(`${API_URL}/api/admin/tag-suggest?${params.toString()}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const suggestions = await response.json();
        setEditForm(prev => ({
          ...prev,
          mood_category: suggestions.mood_category || prev.mood_category,
          mood_tags: [...new Set([...prev.mood_tags, ...suggestions.mood_tags])],
          style_tags: [...new Set([...prev.style_tags, ...suggestions.style_tags])],
          instrument_tags: [...new Set([...prev.instrument_tags, ...suggestions.instrument_tags])],
          vibe: suggestions.vibe || prev.vibe,
          energy: suggestions.energy || prev.energy
        }));
        toast.success('Tag suggestions applied');
      }
    } catch (error) {
      toast.error('Failed to get suggestions');
    }
  };
  
  // Seed demo data
  const seedDemoData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/seed-demo`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Demo data seeded');
        fetchData();
      } else {
        toast.error('Failed to seed data');
      }
    } catch (error) {
      toast.error('Error seeding data');
    }
  };
  
  // ============ User Management Functions ============
  
  const inviteUser = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Email is required');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users/invite`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowInvite(false);
        setInviteEmail('');
        setInviteRole('editor');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send invite');
      }
    } catch (error) {
      toast.error('Error sending invite');
    }
  };
  
  const cancelInvite = async (inviteId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/invite/${inviteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Invite cancelled');
        fetchData();
      } else {
        toast.error('Failed to cancel invite');
      }
    } catch (error) {
      toast.error('Error cancelling invite');
    }
  };
  
  const changeUserRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/role`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole })
      });
      
      if (response.ok) {
        toast.success(`Role changed to ${newRole}`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to change role');
      }
    } catch (error) {
      toast.error('Error changing role');
    }
  };
  
  const toggleUserDisabled = async (userId, isCurrentlyDisabled) => {
    const action = isCurrentlyDisabled ? 'enable' : 'disable';
    if (!isCurrentlyDisabled && !window.confirm(`Are you sure you want to disable this user? They will lose access.`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/disable?disabled=${!isCurrentlyDisabled}`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success(`User ${action}d`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to ${action} user`);
      }
    } catch (error) {
      toast.error(`Error ${action}ing user`);
    }
  };
  
  const removeUserRole = async (userId, email) => {
    if (!window.confirm(`Remove all access for ${email}? They will no longer be able to log in as admin.`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/role`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Role removed');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to remove role');
      }
    } catch (error) {
      toast.error('Error removing role');
    }
  };
  
  // ============ Tag Template Functions ============
  
  const createTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error('Template name is required');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/templates`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newTemplateName,
          ...templateForm
        })
      });
      
      if (response.ok) {
        toast.success('Template created');
        setShowCreateTemplate(false);
        setNewTemplateName('');
        setTemplateForm({});
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create template');
      }
    } catch (error) {
      toast.error('Error creating template');
    }
  };
  
  const deleteTemplate = async (templateId, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/templates/${templateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Template deleted');
        fetchData();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (error) {
      toast.error('Error deleting template');
    }
  };
  
  const applyTemplate = async (templateId, templateName) => {
    if (selectedLoops.length === 0) {
      toast.error('Select loops first');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/templates/${templateId}/apply`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(selectedLoops)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Applied "${templateName}" to ${data.modified_count} loops`);
        setSelectedLoops([]);
        fetchData();
      } else {
        toast.error('Failed to apply template');
      }
    } catch (error) {
      toast.error('Error applying template');
    }
  };
  
  // ============ CSV Import Functions ============
  
  const handleCsvUpload = async (file) => {
    setCsvUploading(true);
    setCsvResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/import/csv`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setCsvResult(data);
        toast.success(data.message);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'CSV import failed');
      }
    } catch (error) {
      toast.error('Error importing CSV');
    } finally {
      setCsvUploading(false);
    }
  };
  
  const downloadCsvTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/export/csv-template`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `LTBB_Loops_Export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('CSV downloaded');
      } else {
        toast.error('Failed to download CSV');
      }
    } catch (error) {
      toast.error('Error downloading CSV');
    }
  };
  
  // ============ Copy Tags Functions ============
  
  const copyTagsFromLoop = async () => {
    if (!copyTagsSource || selectedLoops.length === 0) {
      toast.error('Select a source loop and target loops');
      return;
    }
    
    // Filter out the source from targets
    const targetIds = selectedLoops.filter(id => id !== copyTagsSource.loop_id);
    
    if (targetIds.length === 0) {
      toast.error('Select at least one target loop (different from source)');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/loops/${copyTagsSource.loop_id}/copy-tags`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(targetIds)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowCopyTags(false);
        setCopyTagsSource(null);
        setSelectedLoops([]);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to copy tags');
      }
    } catch (error) {
      toast.error('Error copying tags');
    }
  };
  
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  const loops = activeTab === 'published' ? publishedLoops : unpublishedLoops;
  
  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={PIZZLE_IMAGE} alt="Pizzul" className="w-10 h-10 rounded-full" />
            <div>
              <h1 className="font-heading font-bold text-xl">Admin Dashboard</h1>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              data-testid="library-link"
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="rounded-full"
            >
              <Home className="w-4 h-4 mr-2" />
              Library
            </Button>
            <Link to="/admin/reflect">
              <Button
                data-testid="reflect-admin-link"
                variant="outline"
                size="sm"
                className="rounded-full border-violet-500/50 text-violet-400 hover:bg-violet-500/10"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Reflect
              </Button>
            </Link>
            <Link to="/admin/analytics">
              <Button
                data-testid="analytics-link"
                variant="outline"
                size="sm"
                className="rounded-full border-primary/50 text-primary hover:bg-primary/10"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </Button>
            </Link>
            <Link to="/admin/sound-packs">
              <Button
                data-testid="sound-packs-link"
                variant="outline"
                size="sm"
                className="rounded-full border-accent/50 text-accent hover:bg-accent/10"
              >
                <Package className="w-4 h-4 mr-2" />
                Sound Packs
              </Button>
            </Link>
            <Link to="/admin/metadata">
              <Button
                data-testid="metadata-review-link"
                variant="outline"
                size="sm"
                className="rounded-full border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
              >
                <ListChecks className="w-4 h-4 mr-2" />
                Metadata Review
              </Button>
            </Link>
            <Link to="/admin/tagging">
              <Button
                data-testid="tagging-studio-link"
                variant="outline"
                size="sm"
                className="rounded-full border-violet-500/50 text-violet-400 hover:bg-violet-500/10"
              >
                <Tags className="w-4 h-4 mr-2" />
                Tagging Studio
              </Button>
            </Link>
            <Link to="/admin/programs">
              <Button
                data-testid="programs-link"
                variant="outline"
                size="sm"
                className="rounded-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              >
                <Settings className="w-4 h-4 mr-2" />
                Programs
              </Button>
            </Link>
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="sm"
              onClick={logout}
              className="rounded-full"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <TabsList className="bg-muted/50 flex-wrap">
              <TabsTrigger value="published" className="data-[state=active]:bg-primary data-[state=active]:text-black">
                <Eye className="w-4 h-4 mr-2" />
                Published ({loopStats.published})
              </TabsTrigger>
              <TabsTrigger value="unpublished" className="data-[state=active]:bg-primary data-[state=active]:text-black">
                <EyeOff className="w-4 h-4 mr-2" />
                Unpublished ({loopStats.unpublished})
              </TabsTrigger>
              <TabsTrigger value="staged" className="data-[state=active]:bg-primary data-[state=active]:text-black">
                <FileAudio className="w-4 h-4 mr-2" />
                Import Queue
              </TabsTrigger>
              <TabsTrigger value="templates" className="data-[state=active]:bg-primary data-[state=active]:text-black">
                <Copy className="w-4 h-4 mr-2" />
                Templates
              </TabsTrigger>
              <TabsTrigger value="csv" className="data-[state=active]:bg-primary data-[state=active]:text-black">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                CSV Import
              </TabsTrigger>
              <TabsTrigger value="knock-engine" className="data-[state=active]:bg-orange-500 data-[state=active]:text-black">
                <Zap className="w-4 h-4 mr-2" />
                Knock Engine
              </TabsTrigger>
              <TabsTrigger value="users" className="data-[state=active]:bg-primary data-[state=active]:text-black">
                <Users className="w-4 h-4 mr-2" />
                Users
              </TabsTrigger>
            </TabsList>
            
            <div className="flex items-center gap-2">
              {selectedLoops.length > 0 && (
                <>
                  {/* Bulk Publish - only on unpublished tab */}
                  {activeTab === 'unpublished' && (
                    <Button
                      data-testid="bulk-publish-btn"
                      size="sm"
                      onClick={bulkPublishLoops}
                      className="rounded-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Publish ({selectedLoops.length})
                    </Button>
                  )}
                  {/* Bulk Unpublish - only on published tab */}
                  {activeTab === 'published' && (
                    <Button
                      data-testid="bulk-unpublish-btn"
                      size="sm"
                      variant="outline"
                      onClick={bulkUnpublishLoops}
                      className="rounded-full border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10"
                    >
                      <EyeOff className="w-4 h-4 mr-2" />
                      Unpublish ({selectedLoops.length})
                    </Button>
                  )}
                  <Button
                    data-testid="bulk-edit-btn"
                    size="sm"
                    onClick={() => setShowBulkTag(true)}
                    className="rounded-full bg-secondary hover:bg-secondary/90"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Bulk Edit ({selectedLoops.length})
                  </Button>
                  <Button
                    data-testid="copy-tags-btn"
                    size="sm"
                    variant="outline"
                    onClick={() => setShowCopyTags(true)}
                    className="rounded-full border-accent/50 text-accent hover:bg-accent/10"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Tags
                  </Button>
                  <Button
                    data-testid="bulk-delete-btn"
                    size="sm"
                    variant="outline"
                    onClick={bulkDeleteLoops}
                    className="rounded-full border-destructive/50 text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete ({selectedLoops.length})
                  </Button>
                </>
              )}
            </div>
          </div>
          
          {/* Search Bar & Filters */}
          {(activeTab === 'published' || activeTab === 'unpublished') && (
            <div className="flex gap-4 flex-wrap items-center">
              <div className="relative max-w-md flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  data-testid="admin-search"
                  type="text"
                  placeholder="Search loops..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 rounded-xl bg-muted/50 border-transparent"
                />
              </div>
              
              {/* Instrument Group Filter */}
              <Select
                value={instrumentGroupFilter || 'all'}
                onValueChange={(value) => setInstrumentGroupFilter(value === 'all' ? '' : value)}
              >
                <SelectTrigger data-testid="instrument-group-filter" className="w-40 rounded-xl bg-muted/50 border-transparent">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="Melodies">Melodies</SelectItem>
                  <SelectItem value="Drums">Drums</SelectItem>
                  <SelectItem value="Textures">Textures</SelectItem>
                  <SelectItem value="Sound FX">Sound FX</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Pack Code Filter (Phase 2) */}
              <Select
                value={packCodeFilter || 'all'}
                onValueChange={(value) => setPackCodeFilter(value === 'all' ? '' : value)}
              >
                <SelectTrigger data-testid="pack-code-filter" className="w-44 rounded-xl bg-muted/50 border-transparent border-amber-500/30">
                  <SelectValue placeholder="All Packs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Packs</SelectItem>
                  <SelectItem value="_unassigned">No Pack Assigned</SelectItem>
                  {availablePackCodes.map(pack => (
                    <SelectItem key={pack.pack_code} value={pack.pack_code}>
                      {pack.pack_code} ({pack.loop_count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Batch Filter (Phase 2) */}
              <Select
                value={batchIdFilter || 'all'}
                onValueChange={(value) => setBatchIdFilter(value === 'all' ? '' : value)}
              >
                <SelectTrigger data-testid="batch-filter" className="w-48 rounded-xl bg-muted/50 border-transparent border-purple-500/30">
                  <SelectValue placeholder="All Batches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Batches</SelectItem>
                  {availableBatches.slice(0, 20).map(batch => (
                    <SelectItem key={batch.batch_id} value={batch.batch_id}>
                      {batch.created_at ? new Date(batch.created_at).toLocaleDateString() : 'Unknown'} ({batch.loop_count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Clear Filters Button */}
              {(packCodeFilter || batchIdFilter || instrumentGroupFilter) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setPackCodeFilter('');
                    setBatchIdFilter('');
                    setInstrumentGroupFilter('');
                  }}
                  className="text-muted-foreground hover:text-white"
                  data-testid="clear-filters-btn"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          )}
          
          {/* Published/Unpublished Loops */}
          <TabsContent value="published" className="space-y-4">
            <LoopTable 
              loops={loops} 
              selectedLoops={selectedLoops}
              setSelectedLoops={setSelectedLoops}
              onEdit={openEditDialog}
              onTogglePublish={togglePublish}
              onDelete={deleteLoop}
              loading={loading}
            />
            {/* Load More Button */}
            {!loading && publishedLoops.length > 0 && hasMorePublished && (
              <div className="text-center py-4">
                <Button
                  data-testid="load-more-published"
                  onClick={loadMoreLoops}
                  disabled={loadingMore}
                  variant="outline"
                  className="rounded-full border-white/20 px-8"
                >
                  {loadingMore ? 'Loading...' : `Load More (showing ${publishedLoops.length} of ${loopStats.published})`}
                </Button>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="unpublished" className="space-y-4">
            <LoopTable 
              loops={loops} 
              selectedLoops={selectedLoops}
              setSelectedLoops={setSelectedLoops}
              onEdit={openEditDialog}
              onTogglePublish={togglePublish}
              onDelete={deleteLoop}
              loading={loading}
            />
            {/* Load More Button */}
            {!loading && unpublishedLoops.length > 0 && hasMoreUnpublished && (
              <div className="text-center py-4">
                <Button
                  data-testid="load-more-unpublished"
                  onClick={loadMoreLoops}
                  disabled={loadingMore}
                  variant="outline"
                  className="rounded-full border-white/20 px-8"
                >
                  {loadingMore ? 'Loading...' : `Load More (showing ${unpublishedLoops.length} of ${loopStats.unpublished})`}
                </Button>
              </div>
            )}
          </TabsContent>
          
          {/* Import Queue */}
          <TabsContent value="staged" className="space-y-6">
            {/* Upload Zone */}
            <div 
              data-testid="upload-zone"
              className={`border-2 border-dashed rounded-3xl p-12 text-center transition-colors ${
                uploadProgress.uploading 
                  ? 'border-primary/50 bg-primary/5' 
                  : 'border-white/20 hover:border-primary/50 cursor-pointer'
              }`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (!uploadProgress.uploading) {
                  handleFileUpload(e.dataTransfer.files);
                }
              }}
              onClick={() => !uploadProgress.uploading && document.getElementById('file-input').click()}
            >
              <input
                id="file-input"
                type="file"
                multiple
                accept=".wav,.ogg,.mp3"
                className="hidden"
                onChange={(e) => handleFileUpload(e.target.files)}
              />
              
              {uploadProgress.uploading ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 relative">
                    <div className="w-16 h-16 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                  </div>
                  <h3 className="font-heading font-bold text-xl mb-2">
                    Uploading {uploadProgress.completed} of {uploadProgress.total}
                  </h3>
                  <div className="max-w-xs mx-auto bg-white/10 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-primary h-full transition-all duration-300"
                      style={{ width: `${(uploadProgress.completed / uploadProgress.total) * 100}%` }}
                    />
                  </div>
                  {uploadProgress.failed > 0 && (
                    <p className="text-destructive text-sm mt-2">{uploadProgress.failed} failed</p>
                  )}
                </>
              ) : (
                <>
                  <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-heading font-bold text-xl mb-2">Drag & Drop Files</h3>
                  <p className="text-muted-foreground">
                    Upload paired files: <span className="text-primary">NAME.wav</span> + <span className="text-primary">NAME.preview.ogg</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Each loop needs both WAV (original) and preview (OGG) files with matching names
                  </p>
                </>
              )}
            </div>
            
            {/* Pairing Errors */}
            {pairingErrors.length > 0 && (
              <div className="p-4 rounded-2xl bg-destructive/10 border border-destructive/20">
                <div className="flex items-center gap-2 text-destructive mb-2">
                  <AlertCircle className="w-5 h-5" />
                  <h4 className="font-semibold">File Pairing Issues</h4>
                </div>
                <ul className="text-sm text-destructive/80 space-y-1">
                  {pairingErrors.map((err, i) => (
                    <li key={i}>• {err}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Staged Files List - Show paired files together */}
            {stagedFiles.length > 0 ? (
              <div className="space-y-4">
                {/* Bulk Naming Panel */}
                {(() => {
                  const pairedOriginals = stagedFiles.filter(f => f.file_type === 'original' && f.paired_with);
                  if (pairedOriginals.length < 2) return null;
                  
                  return (
                    <div className="p-4 rounded-2xl bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-heading font-bold">Bulk Auto-Naming</h3>
                            <p className="text-xs text-muted-foreground">
                              {selectedStagedFiles.length > 0 
                                ? `${selectedStagedFiles.length} files selected` 
                                : `${pairedOriginals.length} files ready`}
                            </p>
                          </div>
                        </div>
                        <Button
                          onClick={() => setBulkNamingOpen(!bulkNamingOpen)}
                          variant="outline"
                          size="sm"
                          className="rounded-full border-primary/50 text-primary"
                        >
                          {bulkNamingOpen ? 'Hide Options' : 'Configure'}
                        </Button>
                      </div>
                      
                      {bulkNamingOpen && (
                        <div className="space-y-4 pt-4 border-t border-white/10">
                          {/* File Selection */}
                          <div className="p-3 rounded-xl bg-white/5">
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-xs font-medium">Select Files to Name</label>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setSelectedStagedFiles(pairedOriginals.map(f => f.file_id))}
                                  className="text-xs h-7 px-2"
                                >
                                  Select All
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedStagedFiles([]);
                                  }}
                                  className="text-xs h-7 px-2"
                                >
                                  Clear Selection
                                </Button>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                              {pairedOriginals.map(file => (
                                <Button
                                  key={file.file_id}
                                  size="sm"
                                  variant={selectedStagedFiles.includes(file.file_id) ? 'default' : 'outline'}
                                  onClick={() => {
                                    setSelectedStagedFiles(prev => 
                                      prev.includes(file.file_id)
                                        ? prev.filter(id => id !== file.file_id)
                                        : [...prev, file.file_id]
                                    );
                                  }}
                                  className={`rounded-full text-xs h-7 ${file.suggested_title ? 'border-primary' : ''}`}
                                  title={file.filename || file.file_id}
                                >
                                  {file.suggested_title || file.filename?.split('.')[0]?.slice(0, 15) || file.file_id.slice(-6)}
                                </Button>
                              ))}
                            </div>
                          </div>
                          
                          {/* Quick Preset Buttons */}
                          <div>
                            <label className="text-xs font-medium mb-2 block">Naming Mode</label>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                variant={bulkNamingConfig.generatePrimary && !bulkNamingConfig.generateVariant ? 'default' : 'outline'}
                                onClick={() => setBulkNamingConfig(prev => ({
                                  ...prev,
                                  generatePrimary: true,
                                  generateVariant: false,
                                  autoVariant: false
                                }))}
                                className="rounded-full"
                              >
                                Primary Only
                              </Button>
                              <Button
                                size="sm"
                                variant={bulkNamingConfig.generatePrimary && bulkNamingConfig.generateVariant ? 'default' : 'outline'}
                                onClick={() => setBulkNamingConfig(prev => ({
                                  ...prev,
                                  generatePrimary: true,
                                  generateVariant: true,
                                  autoVariant: true
                                }))}
                                className="rounded-full"
                              >
                                Primary + Variant
                              </Button>
                              <Button
                                size="sm"
                                variant={!bulkNamingConfig.generatePrimary && bulkNamingConfig.generateVariant ? 'default' : 'outline'}
                                onClick={() => setBulkNamingConfig(prev => ({
                                  ...prev,
                                  generatePrimary: false,
                                  generateVariant: true,
                                  autoVariant: true
                                }))}
                                className="rounded-full"
                              >
                                Variant Only
                              </Button>
                            </div>
                          </div>
                          
                          {/* Distribution & Duplicates */}
                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                              <label className="text-xs font-medium">Distribution:</label>
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant={bulkNamingConfig.distribution === 'randomize' ? 'default' : 'outline'}
                                  onClick={() => setBulkNamingConfig(prev => ({ ...prev, distribution: 'randomize' }))}
                                  className="rounded-full text-xs h-7 px-3"
                                >
                                  Randomize
                                </Button>
                                <Button
                                  size="sm"
                                  variant={bulkNamingConfig.distribution === 'sequential' ? 'default' : 'outline'}
                                  onClick={() => setBulkNamingConfig(prev => ({ ...prev, distribution: 'sequential' }))}
                                  className="rounded-full text-xs h-7 px-3"
                                >
                                  Sequential
                                </Button>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id="allowDupes"
                                checked={bulkNamingConfig.allowDuplicates}
                                onChange={(e) => setBulkNamingConfig(prev => ({ ...prev, allowDuplicates: e.target.checked }))}
                                className="rounded"
                              />
                              <label htmlFor="allowDupes" className="text-xs">Allow duplicates</label>
                            </div>
                          </div>
                          
                          {/* Available Names Preview */}
                          {namingBanks && (
                            <div className="p-3 rounded-xl bg-white/5 text-xs text-muted-foreground">
                              Available from bank: <span className="text-primary font-medium">{namingBanks.primary_names?.filter(n => n.enabled).length || 0}</span> primary names, 
                              <span className="text-secondary font-medium ml-1">{namingBanks.variant_names?.filter(n => n.enabled).length || 0}</span> variants
                            </div>
                          )}
                          
                          {/* Bulk Instrument Group Assignment */}
                          <div className="p-3 rounded-xl bg-white/5">
                            <label className="text-xs font-medium mb-2 block">Bulk Assign Instrument Group</label>
                            <div className="flex items-center gap-2">
                              <Select
                                onValueChange={async (group) => {
                                  const filesToTag = selectedStagedFiles.length > 0 
                                    ? selectedStagedFiles 
                                    : pairedOriginals.map(f => f.file_id);
                                  
                                  if (filesToTag.length === 0) {
                                    toast.error('No files selected');
                                    return;
                                  }
                                  
                                  try {
                                    // Persist to backend
                                    const response = await fetch(`${API_URL}/api/admin/staged/bulk-update`, {
                                      method: 'POST',
                                      credentials: 'include',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({
                                        file_ids: filesToTag,
                                        updates: { instrument_group: group }
                                      })
                                    });
                                    
                                    if (response.ok) {
                                      toast.success(`Applied "${group}" to ${filesToTag.length} files`);
                                      // Refresh staged files from backend to ensure consistency
                                      const stagedRes = await fetch(`${API_URL}/api/admin/staged`, { credentials: 'include' });
                                      if (stagedRes.ok) {
                                        const data = await stagedRes.json();
                                        setStagedFiles(data);
                                      }
                                    } else {
                                      const error = await response.json();
                                      toast.error(error.detail || 'Failed to update files');
                                    }
                                  } catch (error) {
                                    console.error('Bulk update error:', error);
                                    toast.error('Failed to update files');
                                  }
                                }}
                              >
                                <SelectTrigger className="flex-1 h-9 rounded-lg bg-white/10 border-white/20">
                                  <SelectValue placeholder="Select group to apply..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Melodies">Melodies</SelectItem>
                                  <SelectItem value="Drums">Drums</SelectItem>
                                  <SelectItem value="Textures">Textures</SelectItem>
                                  <SelectItem value="Sound FX">Sound FX</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              Select files above, then choose a group to apply to all
                            </p>
                          </div>
                          
                          {/* Apply Bulk Naming Button */}
                          <Button
                            onClick={async () => {
                              const filesToName = selectedStagedFiles.length > 0 
                                ? selectedStagedFiles 
                                : pairedOriginals.map(f => f.file_id);
                              
                              if (filesToName.length === 0) {
                                toast.error('No files selected');
                                return;
                              }
                              
                              try {
                                const response = await fetch(`${API_URL}/api/naming/bulk-assign`, {
                                  method: 'POST',
                                  credentials: 'include',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    file_ids: filesToName,
                                    config: bulkNamingConfig
                                  })
                                });
                                
                                if (response.ok) {
                                  const result = await response.json();
                                  toast.success(`Named ${result.named_count} files`);
                                  setSelectedStagedFiles([]);
                                  // Refresh staged files
                                  const stagedRes = await fetch(`${API_URL}/api/admin/staged`, { credentials: 'include' });
                                  if (stagedRes.ok) {
                                    const data = await stagedRes.json();
                                    setStagedFiles(data);
                                  }
                                } else {
                                  const error = await response.json();
                                  toast.error(error.detail || 'Failed to apply bulk naming');
                                }
                              } catch (error) {
                                toast.error('Failed to apply bulk naming');
                              }
                            }}
                            disabled={selectedStagedFiles.length === 0 && pairedOriginals.length === 0}
                            className="w-full rounded-full bg-primary hover:bg-primary/90 text-black"
                          >
                            <Sparkles className="w-4 h-4 mr-2" />
                            Apply Names to {selectedStagedFiles.length > 0 ? selectedStagedFiles.length : pairedOriginals.length} Files
                          </Button>
                          
                          <p className="text-xs text-center text-muted-foreground">
                            You can still name files manually by clicking on individual files below
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}
                
                {/* Group paired files together */}
                {(() => {
                  // Find unique pairs (only show original files to avoid duplicates)
                  const originalFiles = stagedFiles.filter(f => f.file_type === 'original');
                  const unpairedPreviews = stagedFiles.filter(f => f.file_type === 'preview' && !f.paired_with);
                  
                  return (
                    <>
                      {originalFiles.map(file => {
                        const pairedFile = file.paired_with 
                          ? stagedFiles.find(f => f.file_id === file.paired_with)
                          : null;
                        
                        return (
                          <div 
                            key={file.file_id}
                            className={`p-4 rounded-2xl border transition-all ${
                              file.bulk_named 
                                ? 'bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/30' 
                                : pairedFile 
                                  ? 'bg-card/50 border-white/10' 
                                  : 'bg-destructive/5 border-destructive/20'
                            } ${selectedStagedFiles.includes(file.file_id) ? 'ring-2 ring-primary' : ''}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {/* Play/Stop Button */}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={async () => {
                                    if (playingStaged === file.file_id) {
                                      stop();
                                      setPlayingStaged(null);
                                    } else {
                                      // Get preview URL from staged file
                                      if (pairedFile?.storage_key) {
                                        try {
                                          const res = await fetch(`${API_URL}/api/admin/staged/${pairedFile.file_id}/preview-url`, { credentials: 'include' });
                                          if (res.ok) {
                                            const data = await res.json();
                                            if (data.url) {
                                              // Create a temporary loop object for playback
                                              const tempLoop = {
                                                loop_id: file.file_id,
                                                title: file.suggested_title || file.filename,
                                                preview_url: data.url
                                              };
                                              playLoop(tempLoop);
                                              setPlayingStaged(file.file_id);
                                            }
                                          }
                                        } catch (err) {
                                          toast.error('Could not load preview');
                                        }
                                      } else {
                                        toast.error('No preview file available');
                                      }
                                    }
                                  }}
                                  className={`w-10 h-10 rounded-xl flex-shrink-0 ${
                                    playingStaged === file.file_id 
                                      ? 'bg-primary text-black' 
                                      : 'bg-white/10 hover:bg-white/20'
                                  }`}
                                >
                                  {playingStaged === file.file_id ? (
                                    <Square className="w-4 h-4 fill-current" />
                                  ) : (
                                    <Play className="w-4 h-4 fill-current" />
                                  )}
                                </Button>
                                
                                <div className="flex-1 min-w-0">
                                  {/* Inline editable name from naming bank */}
                                  {editingStagedName?.fileId === file.file_id ? (
                                    <div className="flex flex-col gap-2">
                                      <div className="flex items-center gap-2">
                                        {/* Primary Name Selector */}
                                        <Select
                                          value={editingStagedName.primary || ''}
                                          onValueChange={(v) => setEditingStagedName(prev => ({ 
                                            ...prev, 
                                            primary: v,
                                            name: prev.variant ? `${v} (${prev.variant})` : v
                                          }))}
                                        >
                                          <SelectTrigger 
                                            data-testid={`edit-primary-select-${file.file_id}`}
                                            className="w-36 h-8 text-sm rounded-lg bg-white/10 border-primary/50"
                                          >
                                            <SelectValue placeholder="Primary name..." />
                                          </SelectTrigger>
                                          <SelectContent className="max-h-60">
                                            {namingBanks?.primary_names?.filter(n => n.enabled).map(n => (
                                              <SelectItem key={n.name} value={n.name}>{n.name}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        
                                        {/* Variant Selector (Optional) */}
                                        <Select
                                          value={editingStagedName.variant || ''}
                                          onValueChange={(v) => setEditingStagedName(prev => ({ 
                                            ...prev, 
                                            variant: v,
                                            name: v ? `${prev.primary} (${v})` : prev.primary
                                          }))}
                                        >
                                          <SelectTrigger 
                                            data-testid={`edit-variant-select-${file.file_id}`}
                                            className="w-32 h-8 text-sm rounded-lg bg-white/10 border-secondary/50"
                                          >
                                            <SelectValue placeholder="Variant..." />
                                          </SelectTrigger>
                                          <SelectContent className="max-h-60">
                                            <SelectItem value="none">No variant</SelectItem>
                                            {namingBanks?.variant_names?.filter(n => n.enabled).map(n => (
                                              <SelectItem key={n.name} value={n.name}>{n.name}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        
                                        {/* Apply Button */}
                                        <Button
                                          size="sm"
                                          onClick={() => {
                                            if (editingStagedName.primary) {
                                              const finalName = editingStagedName.variant && editingStagedName.variant !== 'none'
                                                ? `${editingStagedName.primary} (${editingStagedName.variant})`
                                                : editingStagedName.primary;
                                              updateStagedFileName(file.file_id, finalName);
                                            }
                                          }}
                                          disabled={!editingStagedName.primary}
                                          className="h-8 rounded-lg bg-primary hover:bg-primary/90 text-black"
                                        >
                                          <Check className="w-4 h-4" />
                                        </Button>
                                        
                                        {/* Cancel Button */}
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => setEditingStagedName(null)}
                                          className="h-8 w-8 p-0 rounded-lg hover:bg-white/10"
                                        >
                                          <X className="w-4 h-4" />
                                        </Button>
                                      </div>
                                      {editingStagedName.primary && (
                                        <p className="text-xs text-muted-foreground">
                                          Preview: <span className="text-primary font-medium">
                                            {editingStagedName.variant && editingStagedName.variant !== 'none'
                                              ? `${editingStagedName.primary} (${editingStagedName.variant})`
                                              : editingStagedName.primary}
                                          </span>
                                        </p>
                                      )}
                                    </div>
                                  ) : (
                                    <>
                                      {/* Show assigned name prominently if bulk named */}
                                      {file.bulk_named && file.suggested_title ? (
                                        <>
                                          <p 
                                            data-testid={`staged-name-${file.file_id}`}
                                            className="font-bold text-primary cursor-pointer hover:underline"
                                            onClick={() => {
                                              // Parse existing name: "Primary (Variant)" or just "Primary"
                                              const match = file.suggested_title.match(/^(.+?)\s*\((.+?)\)$/);
                                              if (match) {
                                                setEditingStagedName({ fileId: file.file_id, primary: match[1], variant: match[2], name: file.suggested_title });
                                              } else {
                                                setEditingStagedName({ fileId: file.file_id, primary: file.suggested_title, variant: '', name: file.suggested_title });
                                              }
                                            }}
                                            title="Click to edit name"
                                          >
                                            {file.suggested_title}
                                            <Edit className="w-3 h-3 ml-1 inline-block opacity-50" />
                                          </p>
                                          <p className="text-xs text-muted-foreground">{file.filename}</p>
                                        </>
                                      ) : (
                                        <>
                                          <p 
                                            data-testid={`staged-name-${file.file_id}`}
                                            className="font-semibold cursor-pointer hover:text-primary transition-colors"
                                            onClick={() => setEditingStagedName({ 
                                              fileId: file.file_id, 
                                              primary: '',
                                              variant: '',
                                              name: '' 
                                            })}
                                            title="Click to choose name from bank"
                                          >
                                            {file.filename}
                                            <Edit className="w-3 h-3 ml-1 inline-block opacity-50" />
                                          </p>
                                          {file.suggested_title && (
                                            <p className="text-xs text-primary">→ {file.suggested_title}</p>
                                          )}
                                        </>
                                      )}
                                    </>
                                  )}
                                  {pairedFile && (
                                    <p className="text-xs text-muted-foreground">
                                      + {pairedFile.filename}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    {file.instrument_group && (
                                      <Badge className="bg-secondary/20 text-secondary border-0 text-xs">
                                        {file.instrument_group}
                                      </Badge>
                                    )}
                                    {file.bulk_named ? (
                                      <Badge className="bg-primary/20 text-primary border-0 text-xs">
                                        <Check className="w-3 h-3 mr-1" />
                                        Named
                                      </Badge>
                                    ) : pairedFile ? (
                                      <Badge className="bg-accent/20 text-accent border-0 text-xs">
                                        <Check className="w-3 h-3 mr-1" />
                                        Paired
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-destructive/20 text-destructive border-0 text-xs">
                                        <AlertCircle className="w-3 h-3 mr-1" />
                                        Missing preview
                                      </Badge>
                                    )}
                                    {playingStaged === file.file_id && (
                                      <Volume2 className="w-3 h-3 text-primary animate-pulse" />
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Right side actions */}
                              <div className="flex items-center gap-2">
                                {/* Instrument Group Selector */}
                                <Select
                                  value={file.instrument_group || ''}
                                  onValueChange={async (v) => {
                                    try {
                                      const response = await fetch(`${API_URL}/api/admin/staged/${file.file_id}`, {
                                        method: 'PATCH',
                                        credentials: 'include',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ instrument_group: v })
                                      });
                                      if (response.ok) {
                                        setStagedFiles(prev => prev.map(f => 
                                          f.file_id === file.file_id ? { ...f, instrument_group: v } : f
                                        ));
                                      }
                                    } catch (error) {
                                      toast.error('Failed to update group');
                                    }
                                  }}
                                >
                                  <SelectTrigger className="w-28 h-8 rounded-full text-xs">
                                    <SelectValue placeholder="Group..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Melodies">Melodies</SelectItem>
                                    <SelectItem value="Drums">Drums</SelectItem>
                                    <SelectItem value="Textures">Textures</SelectItem>
                                    <SelectItem value="Sound FX">Sound FX</SelectItem>
                                  </SelectContent>
                                </Select>
                                
                                {/* Mood Selector */}
                                <Select
                                  value={file.mood_category || ''}
                                  onValueChange={async (v) => {
                                    try {
                                      const response = await fetch(`${API_URL}/api/admin/staged/${file.file_id}`, {
                                        method: 'PATCH',
                                        credentials: 'include',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ mood_category: v })
                                      });
                                      if (response.ok) {
                                        setStagedFiles(prev => prev.map(f => 
                                          f.file_id === file.file_id ? { ...f, mood_category: v } : f
                                        ));
                                      }
                                    } catch (error) {
                                      toast.error('Failed to update mood');
                                    }
                                  }}
                                >
                                  <SelectTrigger className="w-28 h-8 rounded-full text-xs">
                                    <SelectValue placeholder="Mood..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {taxonomy?.mood_categories?.map(mood => (
                                      <SelectItem key={mood} value={mood}>{mood}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                
                                {pairedFile && (
                                  <Button
                                    data-testid={`create-loop-${file.file_id}`}
                                    size="sm"
                                    onClick={() => createLoopFromStaged(file.file_id, file.mood_category)}
                                    disabled={!file.instrument_group}
                                    title={!file.instrument_group ? 'Select an instrument group first' : ''}
                                    className="rounded-full bg-primary hover:bg-primary/90 text-black disabled:opacity-50"
                                  >
                                    <Tag className="w-4 h-4 mr-1" />
                                    {file.bulk_named ? 'Create Loop' : 'Name & Create'}
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => deleteStagedFile(file.file_id)}
                                  className="rounded-full hover:bg-destructive/20 hover:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Show unpaired preview files */}
                      {unpairedPreviews.map(file => (
                        <div 
                          key={file.file_id}
                          className="p-4 rounded-2xl bg-destructive/5 border border-destructive/20"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                                <FileAudio className="w-5 h-5 text-destructive" />
                              </div>
                              <div>
                                <p className="font-semibold">{file.filename}</p>
                                <Badge className="bg-destructive/20 text-destructive border-0 text-xs mt-1">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Missing WAV file
                                </Badge>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteStagedFile(file.file_id)}
                              className="rounded-full hover:bg-destructive/20 hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </>
                  );
                })()}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                No files in import queue
              </div>
            )}
          </TabsContent>
          
          {/* Tag Templates Tab */}
          <TabsContent value="templates" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Tag Templates</h2>
                <p className="text-sm text-muted-foreground">Save and reuse common tag combinations</p>
              </div>
              <Button
                data-testid="create-template-btn"
                onClick={() => setShowCreateTemplate(true)}
                className="rounded-full bg-primary hover:bg-primary/90 text-black"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </div>
            
            {templatesLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-24 rounded-2xl bg-card/50 animate-pulse" />
                ))}
              </div>
            ) : tagTemplates.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {tagTemplates.map(template => (
                  <div 
                    key={template.template_id}
                    className="p-4 rounded-2xl bg-card/50 border border-white/10"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold text-lg">{template.name}</h3>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => applyTemplate(template.template_id, template.name)}
                          disabled={selectedLoops.length === 0}
                          className="rounded-full h-8 hover:bg-primary/20 hover:text-primary"
                          title={selectedLoops.length === 0 ? "Select loops first" : `Apply to ${selectedLoops.length} loops`}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteTemplate(template.template_id, template.name)}
                          className="rounded-full h-8 hover:bg-destructive/20 hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {template.mood_category && (
                        <Badge className="bg-primary/20 text-primary border-0 text-xs">{template.mood_category}</Badge>
                      )}
                      {template.vibe && (
                        <Badge variant="outline" className="text-xs border-white/20">{template.vibe}</Badge>
                      )}
                      {template.energy && (
                        <Badge variant="outline" className="text-xs border-white/20">Energy {template.energy}</Badge>
                      )}
                      {template.style_tags?.map(tag => (
                        <Badge key={tag} className="bg-secondary/20 text-secondary border-0 text-xs">{tag}</Badge>
                      ))}
                      {template.vibe_packs?.map(pack => (
                        <Badge key={pack} className="bg-accent/20 text-accent border-0 text-xs">{pack}</Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Copy className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No templates yet. Create one to speed up tagging!</p>
              </div>
            )}
            
            {selectedLoops.length > 0 && (
              <div className="p-4 rounded-xl bg-primary/10 border border-primary/30">
                <p className="text-sm">
                  <strong>{selectedLoops.length}</strong> loops selected. Click <Check className="w-4 h-4 inline" /> on a template to apply it.
                </p>
              </div>
            )}
          </TabsContent>
          
          {/* CSV Import Tab */}
          <TabsContent value="csv" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">CSV Bulk Import</h2>
                <p className="text-sm text-muted-foreground">Import/update loop metadata from a spreadsheet</p>
              </div>
              <Button
                data-testid="download-csv-template-btn"
                variant="outline"
                onClick={downloadCsvTemplate}
                className="rounded-full border-white/20"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Current Loops
              </Button>
            </div>
            
            <div className="p-6 rounded-2xl bg-card/50 border border-white/10">
              <h3 className="font-semibold mb-2">How it works:</h3>
              <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1 mb-4">
                <li>Click &quot;Export Current Loops&quot; to download existing data as CSV</li>
                <li>Edit the CSV in Google Sheets or Excel (add tags, moods, etc.)</li>
                <li>Upload the modified CSV below to bulk update</li>
              </ol>
              <p className="text-xs text-muted-foreground mb-4">
                <strong>CSV columns:</strong> loop_id, title, bpm, mood_category, mood_tags, style_tags, instrument_tags, vibe, energy, color_pack, vibe_packs, pack_name
              </p>
              <p className="text-xs text-muted-foreground">
                <strong>Note:</strong> Loops are matched by <code className="bg-white/10 px-1 rounded">loop_id</code> (preferred) or <code className="bg-white/10 px-1 rounded">title</code>. Comma-separate multiple tags.
              </p>
            </div>
            
            {/* CSV Upload Zone */}
            <div 
              data-testid="csv-upload-zone"
              className={`border-2 border-dashed rounded-3xl p-12 text-center transition-colors ${
                csvUploading 
                  ? 'border-primary/50 bg-primary/5' 
                  : 'border-white/20 hover:border-primary/50 cursor-pointer'
              }`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (!csvUploading && e.dataTransfer.files[0]) {
                  handleCsvUpload(e.dataTransfer.files[0]);
                }
              }}
              onClick={() => !csvUploading && document.getElementById('csv-input').click()}
            >
              <input
                id="csv-input"
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => e.target.files[0] && handleCsvUpload(e.target.files[0])}
              />
              
              {csvUploading ? (
                <>
                  <div className="w-12 h-12 mx-auto mb-4 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                  <p className="font-semibold">Processing CSV...</p>
                </>
              ) : (
                <>
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-heading font-bold text-xl mb-2">Drop CSV Here</h3>
                  <p className="text-muted-foreground">or click to browse</p>
                </>
              )}
            </div>
            
            {/* CSV Result */}
            {csvResult && (
              <div className="p-4 rounded-2xl bg-card/50 border border-white/10">
                <h3 className="font-semibold mb-3">Import Results</h3>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 rounded-xl bg-white/5">
                    <p className="text-2xl font-bold text-primary">{csvResult.matched}</p>
                    <p className="text-xs text-muted-foreground">Matched</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-white/5">
                    <p className="text-2xl font-bold text-accent">{csvResult.updated}</p>
                    <p className="text-xs text-muted-foreground">Updated</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-white/5">
                    <p className="text-2xl font-bold text-destructive">{csvResult.not_found_count}</p>
                    <p className="text-xs text-muted-foreground">Not Found</p>
                  </div>
                </div>
                {csvResult.not_found?.length > 0 && (
                  <div className="text-sm">
                    <p className="font-semibold mb-1">Not found:</p>
                    <p className="text-muted-foreground">{csvResult.not_found.join(', ')}</p>
                  </div>
                )}
                {csvResult.errors?.length > 0 && (
                  <div className="text-sm mt-2">
                    <p className="font-semibold mb-1 text-destructive">Errors:</p>
                    <p className="text-muted-foreground">{csvResult.errors.join('; ')}</p>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
          
          {/* Knock Engine Tab */}
          <TabsContent value="knock-engine" className="space-y-6">
            <KnockEngineTab />
          </TabsContent>
          
          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">User Management</h2>
                <p className="text-sm text-muted-foreground">Manage roles and access control</p>
              </div>
              <Button
                data-testid="invite-user-btn"
                onClick={() => setShowInvite(true)}
                className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground"
              >
                <Mail className="w-4 h-4 mr-2" />
                Invite User
              </Button>
            </div>
            
            {/* Pending Invites */}
            {pendingInvites.length > 0 && (
              <div className="p-4 rounded-2xl bg-yellow-500/10 border border-yellow-500/30">
                <h3 className="font-semibold text-yellow-500 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Pending Invites ({pendingInvites.length})
                </h3>
                <div className="space-y-2">
                  {pendingInvites.map(invite => (
                    <div key={invite.invite_id} className="flex items-center justify-between p-3 rounded-xl bg-card/50">
                      <div>
                        <p className="font-medium">{invite.email}</p>
                        <p className="text-xs text-muted-foreground">
                          Role: {invite.role} • Invited by {invite.invited_by_name || 'Unknown'}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => cancelInvite(invite.invite_id)}
                        className="rounded-full hover:bg-destructive/20 hover:text-destructive"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Users List */}
            {usersLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-20 rounded-2xl bg-card/50 animate-pulse" />
                ))}
              </div>
            ) : users.length > 0 ? (
              <div className="space-y-2">
                {users.filter(u => u.role).map(u => (
                  <div 
                    key={u.user_id}
                    className={`flex items-center justify-between p-4 rounded-2xl border ${
                      u.is_disabled 
                        ? 'bg-destructive/5 border-destructive/30 opacity-60' 
                        : u.role === 'owner'
                          ? 'bg-accent/10 border-accent/30'
                          : u.role === 'admin'
                            ? 'bg-primary/5 border-primary/30'
                            : 'bg-card/50 border-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {u.picture ? (
                        <img src={u.picture} alt={u.name} className="w-10 h-10 rounded-full" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                          <Users className="w-5 h-5 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold">{u.name}</p>
                          {u.role === 'owner' && (
                            <Badge className="bg-accent/20 text-accent border-0 text-xs">
                              <Crown className="w-3 h-3 mr-1" />
                              Owner
                            </Badge>
                          )}
                          {u.role === 'admin' && (
                            <Badge className="bg-primary/20 text-primary border-0 text-xs">
                              <Shield className="w-3 h-3 mr-1" />
                              Admin
                            </Badge>
                          )}
                          {u.role === 'editor' && (
                            <Badge className="bg-secondary/20 text-secondary border-0 text-xs">
                              <Edit className="w-3 h-3 mr-1" />
                              Editor
                            </Badge>
                          )}
                          {u.is_disabled && (
                            <Badge variant="outline" className="text-xs border-destructive/50 text-destructive">
                              Disabled
                            </Badge>
                          )}
                          {u.user_id === user?.user_id && (
                            <Badge variant="outline" className="text-xs border-white/20">You</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                        {u.invited_by_name && (
                          <p className="text-xs text-muted-foreground">Invited by {u.invited_by_name}</p>
                        )}
                      </div>
                    </div>
                    
                    {u.user_id !== user?.user_id && u.role !== 'owner' && (
                      <div className="flex items-center gap-2">
                        {/* Role Selector */}
                        <Select
                          value={u.role}
                          onValueChange={(newRole) => changeUserRole(u.user_id, newRole)}
                        >
                          <SelectTrigger className="w-28 h-8 rounded-lg bg-muted/50 border-transparent text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="editor">Editor</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        {/* Disable/Enable Toggle */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleUserDisabled(u.user_id, u.is_disabled)}
                          className={`rounded-full ${u.is_disabled ? 'hover:bg-accent/20 hover:text-accent' : 'hover:bg-yellow-500/20 hover:text-yellow-500'}`}
                          title={u.is_disabled ? 'Enable user' : 'Disable user'}
                        >
                          {u.is_disabled ? <Shield className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
                        </Button>
                        
                        {/* Remove Role */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeUserRole(u.user_id, u.email)}
                          className="rounded-full hover:bg-destructive/20 hover:text-destructive"
                          title="Remove all access"
                        >
                          <UserMinus className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No users with roles</p>
              </div>
            )}
            
            {/* Role Permissions Info */}
            <div className="p-4 rounded-2xl bg-card/30 border border-white/10">
              <h3 className="font-semibold mb-3">Role Permissions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="p-3 rounded-xl bg-accent/10">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-4 h-4 text-accent" />
                    <span className="font-semibold text-accent">Owner</span>
                  </div>
                  <p className="text-muted-foreground text-xs">Full access + user management. Cannot be demoted.</p>
                </div>
                <div className="p-3 rounded-xl bg-primary/10">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-primary" />
                    <span className="font-semibold text-primary">Admin</span>
                  </div>
                  <p className="text-muted-foreground text-xs">Upload, tag, publish, export. No user management.</p>
                </div>
                <div className="p-3 rounded-xl bg-secondary/10">
                  <div className="flex items-center gap-2 mb-2">
                    <Edit className="w-4 h-4 text-secondary" />
                    <span className="font-semibold text-secondary">Editor</span>
                  </div>
                  <p className="text-muted-foreground text-xs">Upload and tag only. No publish or export.</p>
                </div>
              </div>
            </div>
            
            {/* Audit Log */}
            {auditLogs.length > 0 && (
              <div className="p-4 rounded-2xl bg-card/30 border border-white/10">
                <h3 className="font-semibold mb-3">Recent Activity</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {auditLogs.map(log => (
                    <div key={log.log_id} className="flex items-center justify-between text-sm p-2 rounded-lg bg-white/5">
                      <div>
                        <span className="font-medium">{log.actor_email}</span>
                        <span className="text-muted-foreground"> {log.action.replace(/_/g, ' ')} </span>
                        <span className="font-medium">{log.target_email}</span>
                        {log.details?.new_role && (
                          <span className="text-muted-foreground"> → {log.details.new_role}</span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(log.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Edit Dialog */}
      <Dialog open={!!editingLoop} onOpenChange={() => setEditingLoop(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Loop</DialogTitle>
          </DialogHeader>
          
          {editingLoop && (
            <div className="space-y-6">
              {/* Instrument Group */}
              <div>
                <label className="block text-sm font-semibold mb-2">Instrument Group</label>
                <div className="grid grid-cols-4 gap-2">
                  {editNamingBanks?.groups?.map(group => (
                    <button
                      key={group}
                      onClick={() => setEditForm(prev => ({ ...prev, instrument_group: group }))}
                      className={`flex items-center justify-center gap-2 p-2 rounded-xl border transition-all text-sm ${
                        editForm.instrument_group === group
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      {group === 'Melodies' && <Music className="w-4 h-4" />}
                      {group === 'Drums' && <Drum className="w-4 h-4" />}
                      {group === 'Textures' && <Waves className="w-4 h-4" />}
                      {group === 'Sound FX' && <Zap className="w-4 h-4" />}
                      {group}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Apply Template - Quick Fill */}
              {editForm.instrument_group && tagTemplates.filter(t => t.instrument_group === editForm.instrument_group).length > 0 && (
                <div className="p-3 rounded-xl bg-accent/5 border border-accent/20">
                  <label className="block text-sm font-semibold mb-2">
                    Quick Fill from Template
                    <span className="text-xs text-muted-foreground ml-2">(fills tags below)</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {tagTemplates
                      .filter(t => t.instrument_group === editForm.instrument_group)
                      .map(template => (
                        <Button
                          key={template.template_id}
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            // Apply template fields to edit form
                            setEditForm(prev => ({
                              ...prev,
                              mood_category: template.mood_category || prev.mood_category,
                              style_tags: template.style_tags || prev.style_tags,
                              energy: template.energy || prev.energy,
                              color_pack: template.color_pack || prev.color_pack,
                              vibe_packs: template.vibe_packs || prev.vibe_packs
                            }));
                            toast.success(`Applied "${template.name}" template`);
                          }}
                          className="rounded-full border-accent/30 hover:bg-accent/10"
                        >
                          <Sparkles className="w-3 h-3 mr-1" />
                          {template.name}
                        </Button>
                      ))}
                  </div>
                </div>
              )}
              
              {/* Loop Name - Using Naming Bank */}
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <label className="block text-sm font-semibold mb-3">Loop Name</label>
                
                {editForm.instrument_group ? (
                  <div className="space-y-3">
                    {/* Base Name Selection */}
                    <div>
                      <label className="block text-xs text-muted-foreground mb-2">Base Name</label>
                      <div className="relative mb-2">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search names..."
                          value={editSearchQuery}
                          onChange={(e) => setEditSearchQuery(e.target.value)}
                          className="pl-9 rounded-xl bg-white/5 border-white/10"
                        />
                      </div>
                      <div className="max-h-32 overflow-y-auto rounded-xl border border-white/10 p-2">
                        <div className="grid grid-cols-4 gap-1">
                          {getEditBaseNames().map(name => (
                            <button
                              key={name}
                              onClick={() => {
                                const { variant } = parseCurrentTitle();
                                setEditForm(prev => ({ ...prev, title: buildTitle(name, variant) }));
                              }}
                              className={`px-2 py-1.5 text-xs rounded-lg transition-all ${
                                parseCurrentTitle().baseName === name
                                  ? 'bg-primary text-black font-medium'
                                  : 'hover:bg-white/10'
                              }`}
                            >
                              {name}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Variant Selection */}
                    <div>
                      <label className="block text-xs text-muted-foreground mb-2">Variant (Optional)</label>
                      <Select 
                        value={parseCurrentTitle().variant || '_none'} 
                        onValueChange={(v) => {
                          const { baseName } = parseCurrentTitle();
                          setEditForm(prev => ({ ...prev, title: buildTitle(baseName, v === '_none' ? '' : v) }));
                        }}
                      >
                        <SelectTrigger className="rounded-xl bg-white/5 border-white/10">
                          <SelectValue placeholder="No variant" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">No variant</SelectItem>
                          {getEditVariants().map(variant => (
                            <SelectItem key={variant} value={variant}>{variant}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Final Name Preview */}
                    <div className={`p-3 rounded-xl ${
                      editNameCollision 
                        ? 'bg-destructive/10 border border-destructive/30' 
                        : 'bg-primary/10 border border-primary/30'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-xs text-muted-foreground">Final Name: </span>
                          <span className="font-semibold">{editForm.title}</span>
                        </div>
                        {editNameCollision ? (
                          <Badge variant="destructive" className="text-xs">Name Exists</Badge>
                        ) : editForm.title !== editingLoop.title ? (
                          <Badge className="bg-primary/20 text-primary border-0 text-xs">Available</Badge>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground text-sm">
                    Select an instrument group first to choose a name
                  </div>
                )}
              </div>
              
              {/* BPM - Required for Melodies and Drums */}
              {(editForm.instrument_group === 'Melodies' || editForm.instrument_group === 'Drums') && (
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    BPM <span className="text-destructive">*</span>
                  </label>
                  <Input
                    data-testid="edit-bpm"
                    type="number"
                    value={editForm.bpm}
                    onChange={(e) => setEditForm(prev => ({ ...prev, bpm: parseInt(e.target.value) }))}
                    className="rounded-xl bg-muted/50 border-transparent w-32"
                  />
                </div>
              )}
              
              {/* BPM - Optional for Textures/Sound FX */}
              {(editForm.instrument_group === 'Textures' || editForm.instrument_group === 'Sound FX') && (
                <div>
                  <label className="block text-sm font-semibold mb-2 text-muted-foreground">
                    BPM (Optional)
                  </label>
                  <Input
                    type="number"
                    value={editForm.bpm || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, bpm: e.target.value ? parseInt(e.target.value) : null }))}
                    className="rounded-xl bg-muted/50 border-transparent w-32"
                    placeholder="N/A"
                  />
                </div>
              )}
              
              {/* Mood Category & Mood Tags - MELODIES ONLY */}
              {editForm.instrument_group === 'Melodies' && (
                <>
                  <div className="p-4 rounded-xl bg-violet/5 border border-violet/20">
                    <label className="block text-sm font-semibold mb-2">
                      Mood Category <span className="text-destructive">*</span>
                      <span className="text-xs text-muted-foreground ml-2">(Melodies only)</span>
                    </label>
                    <Select
                      value={editForm.mood_category}
                      onValueChange={(value) => setEditForm(prev => ({ ...prev, mood_category: value }))}
                    >
                      <SelectTrigger className="rounded-xl bg-muted/50 border-transparent">
                        <SelectValue placeholder="Select mood" />
                      </SelectTrigger>
                      <SelectContent>
                        {taxonomy?.mood_categories?.map(mood => (
                          <SelectItem key={mood} value={mood}>{mood}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  
                    {/* Mood Tags */}
                    {editForm.mood_category && taxonomy?.mood_tags?.[editForm.mood_category] && (
                      <div className="mt-4">
                        <label className="block text-sm font-semibold mb-2">
                          Mood Tags <span className="text-destructive">*</span>
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {taxonomy.mood_tags[editForm.mood_category].map(tag => (
                            <Button
                              key={tag}
                              size="sm"
                              variant={editForm.mood_tags?.includes(tag) ? 'default' : 'outline'}
                              onClick={() => {
                                const tags = editForm.mood_tags || [];
                                setEditForm(prev => ({
                                  ...prev,
                                  mood_tags: tags.includes(tag) 
                                    ? tags.filter(t => t !== tag)
                                    : [...tags, tag]
                                }));
                              }}
                              className={`rounded-full ${editForm.mood_tags?.includes(tag) ? 'bg-primary text-black' : 'border-white/20'}`}
                            >
                              {tag}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Musical Key - Melodies only */}
                  <div className="mt-4">
                    <label className="block text-sm font-semibold mb-2">
                      Musical Key
                      <span className="text-xs text-muted-foreground ml-2">(helps match melodies)</span>
                    </label>
                    <Select
                      value={editForm.key || '_none'}
                      onValueChange={(value) => setEditForm(prev => ({ ...prev, key: value === '_none' ? '' : value }))}
                    >
                      <SelectTrigger aria-label="Musical key" data-testid="edit-key-select" className="rounded-xl bg-muted/50 border-transparent">
                        <SelectValue placeholder="Select key" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">No key set</SelectItem>
                        <div className="px-2 py-1 text-xs text-muted-foreground font-semibold">Major Keys</div>
                        {MUSICAL_KEYS.filter(k => k.endsWith(' maj')).map(key => (
                          <SelectItem key={key} value={key}>{key}</SelectItem>
                        ))}
                        <div className="px-2 py-1 text-xs text-muted-foreground font-semibold mt-1">Minor Keys</div>
                        {MUSICAL_KEYS.filter(k => k.endsWith(' min')).map(key => (
                          <SelectItem key={key} value={key}>{key}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              
              {/* Energy - Required for Drums, Textures, Sound FX */}
              {(editForm.instrument_group === 'Drums' || editForm.instrument_group === 'Textures' || editForm.instrument_group === 'Sound FX') && (
                <div className="p-4 rounded-xl bg-accent/5 border border-accent/20">
                  <label className="block text-sm font-semibold mb-2">
                    Energy <span className="text-destructive">*</span>
                    <span className="text-xs text-muted-foreground ml-2">(Drums / Textures / Sound FX)</span>
                  </label>
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[editForm.energy || 3]}
                      min={1}
                      max={5}
                      step={1}
                      onValueChange={([value]) => setEditForm(prev => ({ ...prev, energy: value }))}
                      className="flex-1"
                    />
                    <span className="text-lg font-bold w-8 text-center">{editForm.energy || 3}</span>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>Low</span>
                    <span>High</span>
                  </div>
                </div>
              )}
              
              {/* Style Tags - Required for ALL */}
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Style/Genre Tags <span className="text-destructive">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {taxonomy?.style_tags?.map(tag => (
                    <Button
                      key={tag}
                      size="sm"
                      variant={editForm.style_tags?.includes(tag) ? 'default' : 'outline'}
                      onClick={() => {
                        const tags = editForm.style_tags || [];
                        setEditForm(prev => ({
                          ...prev,
                          style_tags: tags.includes(tag) 
                            ? tags.filter(t => t !== tag)
                            : [...tags, tag]
                        }));
                      }}
                      className={`rounded-full ${editForm.style_tags?.includes(tag) ? 'bg-secondary text-white' : 'border-white/20'}`}
                    >
                      {tag}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Choose Your Vibe - REQUIRED FOR ALL */}
              <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                <label className="block text-sm font-semibold mb-2">
                  Choose Your Vibe <span className="text-destructive">*</span>
                  <span className="text-xs text-muted-foreground ml-2">(Required for all loops)</span>
                </label>
                <p className="text-xs text-muted-foreground mb-3">
                  Assign this loop to one or more vibe packs for discovery.
                </p>
                <div className="flex flex-wrap gap-2">
                  {vibePacks.map(pack => (
                    <Button
                      key={pack.id}
                      size="sm"
                      variant={editForm.vibe_packs?.includes(pack.name) ? 'default' : 'outline'}
                      onClick={() => {
                        const packs = editForm.vibe_packs || [];
                        setEditForm(prev => ({
                          ...prev,
                          vibe_packs: packs.includes(pack.name)
                            ? packs.filter(p => p !== pack.name)
                            : [...packs, pack.name]
                        }));
                      }}
                      className={`rounded-full ${editForm.vibe_packs?.includes(pack.name) ? 'bg-primary text-black' : 'border-white/20'}`}
                    >
                      {pack.name}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Colour is decided by the instrument group and nothing else:
                  Melodies purple, Drums blue, Textures yellow, Sound FX green.
                  The backend derives it in SQL from instrument_group, so there
                  is no colour to store and no way for the two to disagree.

                  This used to be an editable dropdown marked required. There is
                  no color_pack column, so whatever was picked here was silently
                  discarded on save: staff could set Melodies to Green, watch it
                  save, and nothing happened. Showing the derived value makes the
                  rule visible and removes the only control that appeared to
                  break it. Change the instrument group to change the colour. */}
              <div>
                <label className="block text-sm font-semibold mb-2">Color Pack</label>
                <div className="flex items-center gap-2 rounded-xl bg-muted/30 px-3 py-2">
                  <span className={`w-4 h-4 rounded-full ${dotForGroup(editForm.instrument_group)}`} />
                  <span className={`text-sm font-medium ${textForGroup(editForm.instrument_group)}`}>
                    {GROUP_COLOR_NAME[editForm.instrument_group] || 'Set an instrument group'}
                  </span>
                  <span className="text-xs text-muted-foreground ml-auto">from instrument group</span>
                </div>
              </div>
              
              {/* Pack Name - Optional for ALL */}
              <div>
                <label className="block text-sm font-semibold mb-2 text-muted-foreground">Pack Name (Optional)</label>
                <Input
                  data-testid="edit-pack-name"
                  value={editForm.pack_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, pack_name: e.target.value }))}
                  className="rounded-xl bg-muted/50 border-transparent"
                  placeholder="e.g., Chill Pack Vol.1"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              data-testid="suggest-tags-btn"
              variant="outline"
              onClick={suggestTags}
              className="rounded-full border-white/20"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Suggest Tags
            </Button>
            <Button
              data-testid="save-loop-btn"
              onClick={saveLoop}
              className="rounded-full bg-primary hover:bg-primary/90 text-black"
            >
              <Check className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Tag Dialog */}
      <Dialog open={showBulkTag} onOpenChange={setShowBulkTag}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading">Bulk Edit {selectedLoops.length} Loops</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* BPM - Most Requested Feature */}
            <div className="p-3 rounded-xl bg-primary/10 border border-primary/30">
              <label className="block text-sm font-semibold mb-2 text-primary">BPM (Tempo)</label>
              <Input
                data-testid="bulk-bpm-input"
                type="number"
                min="60"
                max="200"
                value={bulkForm.bpm || ''}
                onChange={(e) => setBulkForm(prev => ({ ...prev, bpm: e.target.value ? parseInt(e.target.value) : null }))}
                className="rounded-xl bg-muted/50 border-transparent"
                placeholder="Enter BPM (60-200)"
              />
              <p className="text-xs text-muted-foreground mt-1">Set the same BPM for all selected loops</p>
            </div>
            
            {/* Instrument Group - Critical for Filtering */}
            <div className="p-3 rounded-xl bg-accent/10 border border-accent/30">
              <label className="block text-sm font-semibold mb-2 text-accent">Instrument Group</label>
              <div className="grid grid-cols-4 gap-2">
                {['Melodies', 'Drums', 'Textures', 'Sound FX'].map(group => (
                  <button
                    key={group}
                    onClick={() => setBulkForm(prev => ({ 
                      ...prev, 
                      instrument_group: prev.instrument_group === group ? null : group 
                    }))}
                    className={`flex items-center justify-center gap-1 p-2 rounded-xl border transition-all text-xs ${
                      bulkForm.instrument_group === group
                        ? 'border-accent bg-accent/20 text-accent'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    {group === 'Melodies' && <Music className="w-3 h-3" />}
                    {group === 'Drums' && <Drum className="w-3 h-3" />}
                    {group === 'Textures' && <Waves className="w-3 h-3" />}
                    {group === 'Sound FX' && <Zap className="w-3 h-3" />}
                    {group}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Required for filtering in Published/Unpublished queues</p>
            </div>
            
            {/* Style Tags (Genre) - For Categorization - MOVED UP FOR VISIBILITY */}
            <div className="p-3 rounded-xl bg-secondary/10 border border-secondary/30">
              <label className="block text-sm font-semibold mb-2 text-secondary">🎵 Style / Genre Tags</label>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {taxonomy?.style_tags?.map(tag => (
                  <Button
                    key={tag}
                    size="sm"
                    variant={(bulkForm.style_tags || []).includes(tag) ? 'default' : 'outline'}
                    onClick={() => {
                      const tags = bulkForm.style_tags || [];
                      setBulkForm(prev => ({
                        ...prev,
                        style_tags: tags.includes(tag) 
                          ? tags.filter(t => t !== tag)
                          : [...tags, tag]
                      }));
                    }}
                    className={`rounded-full text-xs ${(bulkForm.style_tags || []).includes(tag) ? 'bg-secondary text-white' : 'border-white/20'}`}
                  >
                    {tag}
                  </Button>
                ))}
              </div>
              {(bulkForm.style_tags || []).length > 0 && (
                <p className="text-xs text-secondary mt-2">
                  Selected: {(bulkForm.style_tags || []).join(', ')}
                </p>
              )}
            </div>
            
            {/* Musical Key - For Melodies */}
            <div>
              <label className="block text-sm font-semibold mb-2">Musical Key <span className="text-xs text-muted-foreground">(Melodies)</span></label>
              <Select
                value={bulkForm.key || '_none'}
                onValueChange={(value) => setBulkForm(prev => ({ ...prev, key: value === '_none' ? null : value }))}
              >
                <SelectTrigger aria-label="Musical key for selected loops" data-testid="bulk-key-select" className="rounded-xl bg-muted/50 border-transparent">
                  <SelectValue placeholder="Select key" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">No change</SelectItem>
                  <div className="px-2 py-1 text-xs text-muted-foreground font-semibold">Major Keys</div>
                  {MUSICAL_KEYS.filter(k => k.endsWith(' maj')).map(key => (
                    <SelectItem key={key} value={key}>{key}</SelectItem>
                  ))}
                  <div className="px-2 py-1 text-xs text-muted-foreground font-semibold mt-1">Minor Keys</div>
                  {MUSICAL_KEYS.filter(k => k.endsWith(' min')).map(key => (
                    <SelectItem key={key} value={key}>{key}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2">Mood Category</label>
              <Select
                value={bulkForm.mood_category || ''}
                onValueChange={(value) => setBulkForm(prev => ({ ...prev, mood_category: value }))}
              >
                <SelectTrigger className="rounded-xl bg-muted/50 border-transparent">
                  <SelectValue placeholder="Select mood" />
                </SelectTrigger>
                <SelectContent>
                  {taxonomy?.mood_categories?.map(mood => (
                    <SelectItem key={mood} value={mood}>{mood}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2">Vibe</label>
              <Select
                value={bulkForm.vibe || ''}
                onValueChange={(value) => setBulkForm(prev => ({ ...prev, vibe: value }))}
              >
                <SelectTrigger className="rounded-xl bg-muted/50 border-transparent">
                  <SelectValue placeholder="Select vibe" />
                </SelectTrigger>
                <SelectContent>
                  {taxonomy?.vibes?.map(vibe => (
                    <SelectItem key={vibe} value={vibe}>{vibe}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2">Energy Level (1-5)</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map(level => (
                  <button
                    key={level}
                    onClick={() => setBulkForm(prev => ({ ...prev, energy: prev.energy === level ? null : level }))}
                    className={`w-10 h-10 rounded-full font-bold transition-all ${
                      bulkForm.energy === level
                        ? 'bg-primary text-black'
                        : 'bg-muted/50 hover:bg-muted'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Same here: colour follows the instrument group being applied. */}
            <div>
              <label className="block text-sm font-semibold mb-2">Color Pack</label>
              <div className="flex items-center gap-2 rounded-xl bg-muted/30 px-3 py-2">
                <span className={`w-4 h-4 rounded-full ${dotForGroup(bulkForm.instrument_group)}`} />
                <span className={`text-sm font-medium ${textForGroup(bulkForm.instrument_group)}`}>
                  {GROUP_COLOR_NAME[bulkForm.instrument_group] || 'Follows instrument group'}
                </span>
                <span className="text-xs text-muted-foreground ml-auto">from instrument group</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2">Pack Name</label>
              <Input
                value={bulkForm.pack_name || ''}
                onChange={(e) => setBulkForm(prev => ({ ...prev, pack_name: e.target.value }))}
                className="rounded-xl bg-muted/50 border-transparent"
                placeholder="e.g., Chill Pack Vol.1"
              />
            </div>
            
            {/* Pack Code Assignment (Phase 2) */}
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
              <label className="block text-sm font-semibold mb-2 text-amber-400">Pack Code (Phase 2)</label>
              <Select
                value={bulkForm.pack_code || '_none'}
                onValueChange={(value) => setBulkForm(prev => ({ 
                  ...prev, 
                  pack_code: value === '_none' ? null : (value === '_clear' ? '' : value)
                }))}
              >
                <SelectTrigger className="rounded-xl bg-muted/50 border-amber-500/30" data-testid="bulk-pack-code-select">
                  <SelectValue placeholder="Assign to pack..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Don't change</SelectItem>
                  <SelectItem value="_clear">Clear pack assignment</SelectItem>
                  {availablePackCodes.map(pack => (
                    <SelectItem key={pack.pack_code} value={pack.pack_code}>
                      {pack.pack_code} - {pack.pack_name || 'Unnamed'} ({pack.loop_count} loops)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">
                Assign selected loops to an existing pack code for easy retrieval in Pack Builder
              </p>
            </div>
            
            {/* Vibe Packs for Bulk */}
            <div>
              <label className="block text-sm font-semibold mb-2">Choose Your Vibe Packs</label>
              <div className="flex flex-wrap gap-2">
                {vibePacks.map(pack => (
                  <Button
                    key={pack.id}
                    size="sm"
                    variant={(bulkForm.vibe_packs || []).includes(pack.name) ? 'default' : 'outline'}
                    onClick={() => {
                      const packs = bulkForm.vibe_packs || [];
                      setBulkForm(prev => ({
                        ...prev,
                        vibe_packs: packs.includes(pack.name) 
                          ? packs.filter(p => p !== pack.name)
                          : [...packs, pack.name]
                      }));
                    }}
                    className={`rounded-full ${(bulkForm.vibe_packs || []).includes(pack.name) ? 'bg-accent text-accent-foreground' : 'border-white/20'}`}
                  >
                    <span className="mr-1">{pack.emoji}</span>
                    {pack.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowBulkTag(false); setBulkForm({}); }}
              className="rounded-full border-white/20"
            >
              Cancel
            </Button>
            <Button
              onClick={applyBulkTags}
              className="rounded-full bg-primary hover:bg-primary/90 text-black"
            >
              Apply Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Template Dialog */}
      <Dialog open={showCreateTemplate} onOpenChange={setShowCreateTemplate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Tag Template</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2">Template Name *</label>
              <Input
                data-testid="template-name"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                className="rounded-xl bg-muted/50 border-transparent"
                placeholder="e.g., Chill Lo-Fi Melody"
              />
            </div>
            
            {/* Instrument Group Selection */}
            <div>
              <label className="block text-sm font-semibold mb-2">Instrument Group *</label>
              <div className="grid grid-cols-2 gap-2">
                {['Melodies', 'Drums', 'Textures', 'Sound FX'].map(group => (
                  <button
                    key={group}
                    onClick={() => setTemplateForm(prev => ({ ...prev, instrument_group: group }))}
                    className={`flex items-center justify-center gap-2 p-2 rounded-xl border transition-all text-sm ${
                      templateForm.instrument_group === group
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    {group === 'Melodies' && <Music className="w-4 h-4" />}
                    {group === 'Drums' && <Drum className="w-4 h-4" />}
                    {group === 'Textures' && <Waves className="w-4 h-4" />}
                    {group === 'Sound FX' && <Zap className="w-4 h-4" />}
                    {group}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Mood Category & Tags - MELODIES ONLY */}
            {templateForm.instrument_group === 'Melodies' && (
              <div className="p-3 rounded-xl bg-violet/5 border border-violet/20">
                <label className="block text-sm font-semibold mb-2">
                  Mood Category
                  <span className="text-xs text-muted-foreground ml-2">(Melodies only)</span>
                </label>
                <Select
                  value={templateForm.mood_category || ''}
                  onValueChange={(value) => setTemplateForm(prev => ({ ...prev, mood_category: value }))}
                >
                  <SelectTrigger className="rounded-xl bg-muted/50 border-transparent">
                    <SelectValue placeholder="Select mood" />
                  </SelectTrigger>
                  <SelectContent>
                    {taxonomy?.mood_categories?.map(mood => (
                      <SelectItem key={mood} value={mood}>{mood}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Energy - Drums, Textures, Sound FX */}
            {(templateForm.instrument_group === 'Drums' || templateForm.instrument_group === 'Textures' || templateForm.instrument_group === 'Sound FX') && (
              <div className="p-3 rounded-xl bg-accent/5 border border-accent/20">
                <label className="block text-sm font-semibold mb-2">
                  Energy
                  <span className="text-xs text-muted-foreground ml-2">(Drums / Textures / Sound FX)</span>
                </label>
                <Select
                  value={templateForm.energy?.toString() || ''}
                  onValueChange={(value) => setTemplateForm(prev => ({ ...prev, energy: parseInt(value) }))}
                >
                  <SelectTrigger className="rounded-xl bg-muted/50 border-transparent">
                    <SelectValue placeholder="Energy level" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map(e => (
                      <SelectItem key={e} value={e.toString()}>{e}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Style Tags - ALL */}
            <div>
              <label className="block text-sm font-semibold mb-2">Style/Genre Tags</label>
              <div className="flex flex-wrap gap-2">
                {taxonomy?.style_tags?.map(tag => (
                  <Button
                    key={tag}
                    size="sm"
                    variant={(templateForm.style_tags || []).includes(tag) ? 'default' : 'outline'}
                    onClick={() => {
                      const tags = templateForm.style_tags || [];
                      setTemplateForm(prev => ({
                        ...prev,
                        style_tags: tags.includes(tag) 
                          ? tags.filter(t => t !== tag)
                          : [...tags, tag]
                      }));
                    }}
                    className={`rounded-full ${(templateForm.style_tags || []).includes(tag) ? 'bg-secondary text-white' : 'border-white/20'}`}
                  >
                    {tag}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Choose Your Vibe - ALL */}
            <div className="p-3 rounded-xl bg-primary/5 border border-primary/20">
              <label className="block text-sm font-semibold mb-2">
                Choose Your Vibe
                <span className="text-xs text-muted-foreground ml-2">(Required for all)</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {vibePacks.map(pack => (
                  <Button
                    key={pack.id}
                    size="sm"
                    variant={(templateForm.vibe_packs || []).includes(pack.name) ? 'default' : 'outline'}
                    onClick={() => {
                      const packs = templateForm.vibe_packs || [];
                      setTemplateForm(prev => ({
                        ...prev,
                        vibe_packs: packs.includes(pack.name) 
                          ? packs.filter(p => p !== pack.name)
                          : [...packs, pack.name]
                      }));
                    }}
                    className={`rounded-full ${(templateForm.vibe_packs || []).includes(pack.name) ? 'bg-primary text-black' : 'border-white/20'}`}
                  >
                    {pack.name}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Color Pack - ALL */}
            <div>
              <label className="block text-sm font-semibold mb-2">Color Pack</label>
              <Select
                value={templateForm.color_pack || ''}
                onValueChange={(value) => setTemplateForm(prev => ({ ...prev, color_pack: value }))}
              >
                <SelectTrigger className="rounded-xl bg-muted/50 border-transparent">
                  <SelectValue placeholder="Select color" />
                </SelectTrigger>
                <SelectContent>
                  {taxonomy?.color_packs?.map(color => (
                    <SelectItem key={color} value={color}>{color}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setShowCreateTemplate(false);
                setNewTemplateName('');
                setTemplateForm({});
              }}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={createTemplate}
              disabled={!templateForm.instrument_group}
              className="rounded-full bg-primary hover:bg-primary/90 text-black"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Copy Tags Dialog */}
      <Dialog open={showCopyTags} onOpenChange={(open) => {
        setShowCopyTags(open);
        if (!open) setCopyTagsSource(null);
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading">Copy Tags From Loop</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select a source loop to copy its tags to the <strong>{selectedLoops.length}</strong> selected loops.
            </p>
            
            <div>
              <label className="block text-sm font-semibold mb-2">Source Loop (copy tags from)</label>
              <div className="max-h-64 overflow-y-auto space-y-2 p-2 rounded-xl bg-muted/30">
                {loops.map(loop => (
                  <div
                    key={loop.loop_id}
                    onClick={() => setCopyTagsSource(loop)}
                    className={`p-3 rounded-xl cursor-pointer transition-colors ${
                      copyTagsSource?.loop_id === loop.loop_id
                        ? 'bg-accent/20 border border-accent/50'
                        : 'bg-card/50 border border-white/10 hover:bg-card/70'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{loop.title}</p>
                        <p className="text-xs text-muted-foreground">{loop.bpm} BPM</p>
                      </div>
                      {copyTagsSource?.loop_id === loop.loop_id && (
                        <Check className="w-5 h-5 text-accent" />
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {loop.mood_category && (
                        <Badge className="bg-primary/20 text-primary border-0 text-xs">{loop.mood_category}</Badge>
                      )}
                      {loop.vibe && (
                        <Badge variant="outline" className="text-xs border-white/20">{loop.vibe}</Badge>
                      )}
                      {loop.style_tags?.slice(0, 2).map(tag => (
                        <Badge key={tag} className="bg-secondary/20 text-secondary border-0 text-xs">{tag}</Badge>
                      ))}
                      {loop.style_tags?.length > 2 && (
                        <Badge variant="outline" className="text-xs border-white/20">+{loop.style_tags.length - 2}</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {copyTagsSource && (
              <div className="p-3 rounded-xl bg-accent/10 border border-accent/30">
                <p className="text-sm">
                  <strong>Will copy:</strong> {copyTagsSource.mood_category || 'No mood'}, {copyTagsSource.vibe || 'No vibe'}, 
                  {copyTagsSource.style_tags?.length || 0} style tags, Energy {copyTagsSource.energy || '-'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  To {selectedLoops.filter(id => id !== copyTagsSource.loop_id).length} target loops
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setShowCopyTags(false);
                setCopyTagsSource(null);
              }}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={copyTagsFromLoop}
              disabled={!copyTagsSource}
              className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Tags
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Invite User Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="max-w-md bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading">Invite User</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Send an invite to grant admin access. They&apos;ll get access when they sign in with this email.
            </p>
            
            <div>
              <label className="block text-sm font-semibold mb-2">Email Address *</label>
              <Input
                data-testid="invite-email-input"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="rounded-xl bg-muted/50 border-transparent"
                placeholder="user@example.com"
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2">Role</label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="rounded-xl bg-muted/50 border-transparent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Admin - Full access (no user management)
                    </div>
                  </SelectItem>
                  <SelectItem value="editor">
                    <div className="flex items-center gap-2">
                      <Edit className="w-4 h-4" />
                      Editor - Upload & tag only
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowInvite(false)} className="rounded-full">
              Cancel
            </Button>
            <Button
              onClick={inviteUser}
              className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Loop Naming Dialog */}
      <LoopNamingDialog
        isOpen={showNamingDialog}
        onClose={() => {
          setShowNamingDialog(false);
          setNamingPairedFile(null);
        }}
        pairedFile={namingPairedFile}
        onSubmit={createLoopWithName}
      />
    </div>
  );
};

// Loop Table Component
const LoopTable = ({ loops, selectedLoops, setSelectedLoops, onEdit, onTogglePublish, onDelete, loading }) => {
  const [playingId, setPlayingId] = useState(null);
  const audioRef = React.useRef(null);
  
  const toggleSelect = (loopId) => {
    setSelectedLoops(prev => 
      prev.includes(loopId) 
        ? prev.filter(id => id !== loopId)
        : [...prev, loopId]
    );
  };
  
  const toggleSelectAll = () => {
    if (selectedLoops.length === loops.length) {
      setSelectedLoops([]);
    } else {
      setSelectedLoops(loops.map(l => l.loop_id));
    }
  };
  
  const togglePlay = (loop) => {
    if (playingId === loop.loop_id) {
      // Stop playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingId(null);
    } else {
      // Stop current audio if playing
      if (audioRef.current) {
        audioRef.current.pause();
      }
      // Play new audio
      const audio = new Audio(loop.preview_url);
      audio.play();
      audio.onended = () => setPlayingId(null);
      audioRef.current = audio;
      setPlayingId(loop.loop_id);
    }
  };
  
  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);
  
  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-16 rounded-2xl bg-card/50 animate-pulse" />
        ))}
      </div>
    );
  }
  
  if (loops.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No loops found
      </div>
    );
  }
  
  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center gap-4 px-4 py-2 text-sm text-muted-foreground">
        <Checkbox
          checked={selectedLoops.length === loops.length && loops.length > 0}
          onCheckedChange={toggleSelectAll}
        />
        <span className="w-10"></span>
        <span className="flex-1">Title</span>
        <span className="w-16 text-center">BPM</span>
        <span className="w-24">Mood</span>
        <span className="w-20">Group</span>
        <span className="w-32">Actions</span>
      </div>
      
      {/* Rows */}
      {loops.map(loop => (
        <div 
          key={loop.loop_id}
          className={`flex items-center gap-4 px-4 py-3 rounded-2xl transition-colors ${
            selectedLoops.includes(loop.loop_id) 
              ? 'bg-primary/10 border border-primary/30' 
              : playingId === loop.loop_id
                ? 'bg-accent/10 border border-accent/30'
                : 'bg-card/50 border border-white/5 hover:bg-card/70'
          }`}
        >
          <Checkbox
            checked={selectedLoops.includes(loop.loop_id)}
            onCheckedChange={() => toggleSelect(loop.loop_id)}
          />
          {/* Play Button */}
          <Button
            size="icon"
            variant="ghost"
            onClick={() => togglePlay(loop)}
            className={`rounded-full h-10 w-10 ${
              playingId === loop.loop_id 
                ? 'bg-accent text-accent-foreground hover:bg-accent/80' 
                : 'hover:bg-white/10'
            }`}
            disabled={!loop.preview_url}
          >
            {playingId === loop.loop_id ? (
              <span className="w-3 h-3 bg-white rounded-sm"></span>
            ) : (
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </Button>
          <div className="flex-1 min-w-0">
            <p className="font-semibold truncate">{loop.title}</p>
            <div className="flex items-center gap-1 mt-1">
              {loop.style_tags?.slice(0, 2).map(tag => (
                <Badge key={tag} variant="outline" className="text-xs border-white/20">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
          <span className="w-16 text-center text-muted-foreground">{loop.bpm || '-'}</span>
          <span className="w-24">
            {loop.mood_category && (
              <Badge className="bg-primary/20 text-primary border-0 text-xs">
                {loop.mood_category}
              </Badge>
            )}
          </span>
          <span className="w-20 text-xs text-muted-foreground">{loop.instrument_group || '-'}</span>
          <div className="w-32 flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onEdit(loop)}
              aria-label={`Edit ${loop.title}`}
              className="rounded-full h-8 w-8 hover:bg-white/10"
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onTogglePublish(loop)}
              className="rounded-full h-8 w-8 hover:bg-white/10"
            >
              {loop.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onDelete(loop.loop_id)}
              className="rounded-full h-8 w-8 hover:bg-destructive/20 hover:text-destructive"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Knock Engine Management Tab
const KnockEngineTab = () => {
  const [assets, setAssets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [tagOptions, setTagOptions] = useState({ style_tags: [], mood_tags: [] });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    root_behavior: 'sub',
    style_tags: [],
    mood_tags: []
  });
  
  const ROOT_BEHAVIORS = ['sub', '808', 'moog', 'warm', 'bass_guitar', 'glide', 'punch'];
  
  // Auto-generate title from selections
  const generateTitle = (behavior, moods, styles) => {
    const behaviorLabel = behavior.charAt(0).toUpperCase() + behavior.slice(1).replace('_', ' ');
    const moodStr = moods.slice(0, 2).join(' ');
    const styleStr = styles.length > 0 ? styles[0] : '';
    return `${behaviorLabel} – ${moodStr} ${styleStr}`.trim().replace(/\s+/g, ' ');
  };
  
  // Update title when selections change
  const updateFormWithAutoTitle = (field, value) => {
    const newForm = { ...uploadForm, [field]: value };
    if (field !== 'title') {
      newForm.title = generateTitle(newForm.root_behavior, newForm.mood_tags, newForm.style_tags);
    }
    setUploadForm(newForm);
  };
  
  // Fetch data
  useEffect(() => {
    fetchData();
    fetchTagOptions();
  }, []);
  
  const fetchTagOptions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/knock-engine/tag-options`);
      if (response.ok) {
        const data = await response.json();
        setTagOptions(data);
      }
    } catch (error) {
      console.error('Failed to fetch tag options:', error);
    }
  };
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [assetsRes, templatesRes, profilesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/knock-engine/assets`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/knock-engine/templates`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/knock-engine/profiles`, { credentials: 'include' })
      ]);
      
      if (assetsRes.ok) setAssets(await assetsRes.json());
      if (templatesRes.ok) setTemplates(await templatesRes.json());
      if (profilesRes.ok) setProfiles(await profilesRes.json());
    } catch (error) {
      console.error('Failed to fetch Knock Engine data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Seed templates
  const seedTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/knock-engine/seed-templates`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to seed templates');
    }
  };
  
  // Upload asset
  const uploadAsset = async (e) => {
    e.preventDefault();
    const fileInput = document.getElementById('ke-file-input');
    if (!fileInput.files[0]) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', fileInput.files[0]);
      formData.append('title', uploadForm.title);
      formData.append('root_behavior', uploadForm.root_behavior);
      formData.append('style_tags', uploadForm.style_tags.join(','));
      formData.append('mood_tags', uploadForm.mood_tags.join(','));
      
      const response = await fetch(`${API_URL}/api/admin/knock-engine/assets/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        toast.success('Asset uploaded successfully');
        setShowUpload(false);
        setUploadForm({ title: '', root_behavior: 'sub', style_tags: [], mood_tags: [] });
        fileInput.value = '';
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload');
      }
    } catch (error) {
      toast.error('Failed to upload asset');
    } finally {
      setUploading(false);
    }
  };
  
  // Toggle asset active state
  const toggleAssetActive = async (asset) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/knock-engine/assets/${asset.asset_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !asset.is_active })
      });
      if (response.ok) {
        fetchData();
        toast.success(asset.is_active ? 'Asset deactivated' : 'Asset activated');
      }
    } catch (error) {
      toast.error('Failed to update asset');
    }
  };
  
  // Delete asset
  const deleteAsset = async (assetId) => {
    if (!window.confirm('Delete this asset?')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/knock-engine/assets/${assetId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Asset deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-orange-400" />
      </div>
    );
  }
  
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-orange-400" />
            Knock Engine
          </h2>
          <p className="text-sm text-muted-foreground">
            Manage bass assets, rhythm templates, and profiles for bassline generation
          </p>
        </div>
        <div className="flex items-center gap-2">
          {templates.length === 0 && (
            <Button
              onClick={seedTemplates}
              variant="outline"
              className="rounded-full border-orange-500/50 text-orange-400"
              data-testid="seed-templates-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Seed Templates
            </Button>
          )}
          <Button
            onClick={() => setShowUpload(true)}
            className="rounded-full bg-orange-500 hover:bg-orange-600 text-black"
            data-testid="upload-asset-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Bass Sound
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/30">
          <p className="text-2xl font-bold text-orange-400">{assets.length}</p>
          <p className="text-sm text-muted-foreground">Bass Assets</p>
        </div>
        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
          <p className="text-2xl font-bold text-amber-400">{templates.length}</p>
          <p className="text-sm text-muted-foreground">Rhythm Templates</p>
        </div>
        <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
          <p className="text-2xl font-bold text-yellow-400">{profiles.length}</p>
          <p className="text-sm text-muted-foreground">Profiles</p>
        </div>
      </div>
      
      {/* Assets List */}
      <div>
        <h3 className="text-md font-semibold mb-3">Bass Assets (One-Shots)</h3>
        {assets.length === 0 ? (
          <div className="text-center py-8 rounded-xl border border-dashed border-white/10">
            <Zap className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
            <p className="text-muted-foreground">No bass assets uploaded yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Upload one-shot bass sounds (WAV/MP3) in root key C
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {assets.map(asset => (
              <div
                key={asset.asset_id}
                className={`flex items-center justify-between p-3 rounded-xl border ${
                  asset.is_active ? 'bg-orange-500/5 border-orange-500/30' : 'bg-muted/30 border-white/10 opacity-60'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                    <Volume2 className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="font-medium">{asset.title}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Badge className="bg-orange-500/20 text-orange-400 border-0">{asset.root_behavior}</Badge>
                      <span>Base: C</span>
                      {asset.style_tags?.length > 0 && (
                        <span>• {asset.style_tags.join(', ')}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {asset.preview_url && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => new Audio(asset.preview_url).play()}
                      className="rounded-full"
                    >
                      <Play className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleAssetActive(asset)}
                    className={asset.is_active ? 'text-green-400' : 'text-muted-foreground'}
                  >
                    {asset.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteAsset(asset.asset_id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Rhythm Templates List */}
      <div>
        <h3 className="text-md font-semibold mb-3">Rhythm Templates</h3>
        {templates.length === 0 ? (
          <div className="text-center py-8 rounded-xl border border-dashed border-white/10">
            <Music className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
            <p className="text-muted-foreground">No rhythm templates</p>
            <p className="text-xs text-muted-foreground mt-1">
              Click "Seed Templates" to add pre-built patterns
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {templates.map(template => (
              <div
                key={template.template_id}
                className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/30"
              >
                <p className="font-medium text-amber-400">{template.name}</p>
                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                  <span>{template.density}</span>
                  <span>•</span>
                  <span>{template.bpm_min}-{template.bpm_max} BPM</span>
                </div>
                {template.style_tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {template.style_tags.slice(0, 2).map(tag => (
                      <Badge key={tag} className="text-xs bg-white/10 border-0">{tag}</Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-400" />
              Upload Bass One-Shot
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={uploadAsset} className="space-y-4 overflow-y-auto flex-1 pr-2">
            <div>
              <label className="block text-sm font-medium mb-1">Audio File</label>
              <Input
                id="ke-file-input"
                type="file"
                accept=".wav,.mp3,.ogg"
                className="rounded-lg"
                data-testid="ke-file-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                WAV, MP3, or OGG • Must be in root key C
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Root Behavior</label>
              <Select
                value={uploadForm.root_behavior}
                onValueChange={(v) => updateFormWithAutoTitle('root_behavior', v)}
              >
                <SelectTrigger className="rounded-lg" data-testid="ke-behavior-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROOT_BEHAVIORS.map(b => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Style Tags</label>
              <div className="flex flex-wrap gap-1.5 p-2 rounded-lg bg-muted/30 border border-white/10 max-h-24 overflow-y-auto">
                {tagOptions.style_tags.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      const newTags = uploadForm.style_tags.includes(tag)
                        ? uploadForm.style_tags.filter(t => t !== tag)
                        : [...uploadForm.style_tags, tag];
                      updateFormWithAutoTitle('style_tags', newTags);
                    }}
                    className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
                      uploadForm.style_tags.includes(tag)
                        ? 'bg-orange-500 text-black'
                        : 'bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Mood Tags</label>
              <div className="flex flex-wrap gap-1.5 p-2 rounded-lg bg-muted/30 border border-white/10 max-h-24 overflow-y-auto">
                {tagOptions.mood_tags.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      const newTags = uploadForm.mood_tags.includes(tag)
                        ? uploadForm.mood_tags.filter(t => t !== tag)
                        : [...uploadForm.mood_tags, tag];
                      updateFormWithAutoTitle('mood_tags', newTags);
                    }}
                    className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
                      uploadForm.mood_tags.includes(tag)
                        ? 'bg-amber-500 text-black'
                        : 'bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">
                Title <span className="text-xs text-muted-foreground">(auto-generated)</span>
              </label>
              <Input
                value={uploadForm.title}
                onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Select options above to auto-generate"
                className="rounded-lg"
                required
                data-testid="ke-title-input"
              />
            </div>
            
            <DialogFooter className="flex-shrink-0 pt-2">
              <Button type="button" variant="ghost" onClick={() => setShowUpload(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={uploading || !uploadForm.title}
                className="bg-orange-500 hover:bg-orange-600 text-black"
                data-testid="ke-upload-submit"
              >
                {uploading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Re-export LoopNamingDialog for use in AdminDashboard
export { default as LoopNamingDialogComponent } from '../components/LoopNamingDialog';

export default AdminDashboard;
